import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CheckCircleOutline, HighlightOff } from "@mui/icons-material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { API_ENDPOINT } from "../../../services/api/resources";
import SearchBar from "../../../components/common/SearchBar/SearchBar";
import PaginantionComponent from "../../../components/common/Pagination/PaginationComponent";
import viewIcon from "../../../assets/images/svg/viewtech.svg";
import styles from "./InvoiceProcess.module.css";
import { nextPageClick, previouPageClick } from "../../../utils/pagination";

const ROWS_PER_PAGE = 10;
const approvalButtonSx = {
  minWidth: 132,
  minHeight: 44,
  px: 2.5,
  borderRadius: "10px",
  fontSize: "14px",
  fontFamily: "GilroyBold",
  letterSpacing: "normal",
  textTransform: "none",
  boxShadow: "none",
  lineHeight: 1.2,
  "& .MuiButton-startIcon": { display: "flex", mr: 1, ml: 0 },
  "&.Mui-focusVisible": { outline: "3px solid #93c5fd", outlineOffset: 3 },
  "&.Mui-disabled": { backgroundColor: "#f1f5f9", borderColor: "#e2e8f0", color: "#64748b" },
};

const COMPARISON_CHECKS = [
  { key: "po_number_match", label: "PO ID" },
  { key: "item_count_match", label: "Item Count" },
  { key: "quantity_match", label: "Quantity" },
  { key: "item_name_match", label: "Item Name" },
  { key: "item_amount_match", label: "Item Amount" },
  { key: "description_match", label: "Description" },
  { key: "total_amount_match", label: "Total Amount" },
];

const CHECK_LABEL_BY_KEY = COMPARISON_CHECKS.reduce((acc, item) => {
  acc[item.key] = item.label;
  return acc;
}, {});

const safeString = (value) =>
  value === undefined || value === null ? "" : String(value);

const safeLower = (value) => safeString(value).toLowerCase();

const valueOrDash = (value) => {
  if (value === undefined || value === null || String(value).trim() === "") {
    return "-";
  }
  return value;
};

const parseJsonValue = (value, fallback) => {
  if (typeof value !== "string") return value ?? fallback;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const toArray = (value) => {
  const parsed = parseJsonValue(value, value);
  return Array.isArray(parsed) ? parsed : [];
};

const pick = (obj, keys) => {
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return value;
    }
  }
  return undefined;
};

const parseNumber = (value) => {
  if (value === undefined || value === null || value === "") return null;
  const numberValue = Number(String(value).replace(/[^\d.-]/g, ""));
  return Number.isFinite(numberValue) ? numberValue : null;
};

const formatNumber = (value) => {
  const numberValue = parseNumber(value);
  if (numberValue === null) return valueOrDash(value);

  return numberValue.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
};

const getCurrencyLabel = (currency = "INR") =>
  safeLower(currency) === "inr" ? "₹" : currency || "₹";

const formatCurrency = (value, currency = "INR") => {
  const numberValue = parseNumber(value);
  if (numberValue === null) return "-";

  return `${getCurrencyLabel(currency)} ${numberValue.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (value) => {
  if (!value) return "-";

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;

  return parsedDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "-";

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return value;

  return parsedDate.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const normalizeToken = (value) =>
  safeLower(value)
    .replace(/[^a-z0-9]/g, "")
    .trim();

const getVerificationSummary = (row) =>
  parseJsonValue(
    row?.verification_summary ?? row?.verification?.verification_summary,
    row?.verification_summary ?? row?.verification?.verification_summary ?? {},
  ) || {};

const getInvoiceItems = (row) =>
  toArray(
    row?.items ||
      row?.invoice?.items ||
      row?.invoice?.line_items ||
      row?.items_raw ||
      row?.line_items ||
      row?.invoice_items ||
      row?.invoice_line_items,
  );

const getPoDataRows = (row) => {
  const parsed = parseJsonValue(row?.po_data, row?.po_data);
  if (Array.isArray(parsed)) return parsed;
  if (parsed && typeof parsed === "object") return [parsed];
  return [];
};

const getPoItems = (row) =>
  (() => {
    const direct = toArray(
      row?.po_items ||
        row?.po_line_items ||
        row?.purchase_order_items ||
        row?.purchase_order?.items,
    );
    if (direct.length) return direct;

    const poData = getPoDataRows(row);
    const fromPoData = poData.flatMap((entry) =>
      toArray(
        entry?.items ||
          entry?.line_items ||
          entry?.po_items ||
          entry?.po_line_items,
      ),
    );

    return fromPoData;
  })();

const getInvoiceNumber = (row) =>
  valueOrDash(pick(row, ["invoice_number", "invoice_no", "bill_number"]));

const getPoNumber = (row) =>
  valueOrDash(
    pick(row, [
      "pi_po_number",
      "po_id",
      "po_number",
      "po_no",
      "purchase_order_no",
      "PO_number",
    ]),
  );

const getInvoiceDate = (row) =>
  pick(row, ["invoice_date", "bill_date", "date"]);

const getUploadedTimestamp = (row) =>
  pick(row, ["created_at", "extracted_at", "uploaded_at"]);

const getInvoiceTotal = (row) =>
  pick(row, ["grand_total", "invoice_amount", "total_amount", "amount"]);

const getCurrency = (row) => pick(row, ["currency", "currency_type"]) || "INR";

const getItemCount = (row) => {
  const items = getInvoiceItems(row);
  const summary = getVerificationSummary(row);
  return (
    items.length ||
    pick(row, ["item_count", "items_count"]) ||
    summary?.item_count_match?.invoice_item_count ||
    "-"
  );
};

const getPoTotal = (row) => {
  const summary = getVerificationSummary(row);
  return (
    summary?.total_amount_match?.po_total_amount ||
    pick(row, ["po_grand_total", "po_total_amount", "purchase_order_total"])
  );
};

const getTotalDifference = (row) => {
  const summary = getVerificationSummary(row);
  const explicitDifference = summary?.total_amount_match?.difference;
  if (explicitDifference !== undefined && explicitDifference !== null) {
    return explicitDifference;
  }

  const invoiceTotal = parseNumber(getInvoiceTotal(row));
  const poTotal = parseNumber(getPoTotal(row));
  if (invoiceTotal === null || poTotal === null) return null;

  return invoiceTotal - poTotal;
};

const normalizeComparisonInvoice = (entry) => {
  if (!entry || typeof entry !== "object") return entry;

  const invoice =
    entry?.invoice && typeof entry.invoice === "object" ? entry.invoice : null;
  const verification =
    entry?.verification && typeof entry.verification === "object"
      ? entry.verification
      : null;
  const approvals =
    entry?.approvals && typeof entry.approvals === "object"
      ? entry.approvals
      : null;

  if (!invoice && !verification && !approvals) return entry;

  return {
    ...(entry || {}),
    ...(invoice || {}),
    ...(approvals || {}),
    match_status: verification?.match_status ?? entry?.match_status,
    verification_status:
      verification?.verification_status ?? entry?.verification_status,
    verification_summary:
      verification?.verification_summary ??
      entry?.verification_summary ??
      invoice?.verification_summary,
    po_data: entry?.po_data ?? invoice?.po_data,
  };
};

const normalizeApiInvoices = (responseData = {}) => {
  if (Array.isArray(responseData))
    return responseData.map(normalizeComparisonInvoice);
  if (Array.isArray(responseData?.response?.invoice_reconciliation?.invoices))
    return responseData.response.invoice_reconciliation.invoices.map(
      normalizeComparisonInvoice,
    );
  if (Array.isArray(responseData?.invoices))
    return responseData.invoices.map(normalizeComparisonInvoice);
  if (Array.isArray(responseData?.approval_queue))
    return responseData.approval_queue.map(normalizeComparisonInvoice);
  if (Array.isArray(responseData?.comparision))
    return responseData.comparision.map(normalizeComparisonInvoice);
  if (Array.isArray(responseData?.comparison))
    return responseData.comparison.map(normalizeComparisonInvoice);

  if (Array.isArray(responseData?.results)) {
    return responseData.results.flatMap((row) =>
      Array.isArray(row?.invoices)
        ? row.invoices.map(normalizeComparisonInvoice)
        : normalizeComparisonInvoice(row),
    );
  }

  if (responseData?.invoice_token || responseData?.invoice_number) {
    return [normalizeComparisonInvoice(responseData)];
  }

  if (
    responseData?.invoice &&
    (responseData.invoice?.invoice_token ||
      responseData.invoice?.invoice_number)
  ) {
    return [normalizeComparisonInvoice(responseData)];
  }

  return [];
};

const getResponseCount = (responseData = {}) => {
  const count =
    responseData?.response?.invoice_reconciliation?.summary
      ?.total_invoice_count ??
    responseData?.count ??
    responseData?.total_count ??
    responseData?.totalCount ??
    responseData?.pagination?.total_items ??
    responseData?.pagination?.totalItems;

  if (count == null) return null;
  const n = Number(count);
  return Number.isFinite(n) ? n : null;
};

const getRowTokenKey = (row) =>
  normalizeToken(pick(row, ["invoice_token", "token", "id"]));

const getRowKey = (row, index) => {
  const invoiceNumberRaw = pick(row, [
    "invoice_number",
    "invoice_no",
    "bill_number",
  ]);
  const poNumberRaw = pick(row, [
    "pi_po_number",
    "po_id",
    "po_number",
    "po_no",
    "purchase_order_no",
    "PO_number",
  ]);
  const invoiceDateRaw = pick(row, ["invoice_date", "bill_date", "date"]);

  const invoiceNumberKey = normalizeToken(invoiceNumberRaw);
  const poNumberKey = normalizeToken(poNumberRaw);
  const normalizeInvoiceDateKey = (value) => {
    if (!value) return "";
    const raw = String(value).trim();
    const match = raw.match(/^(\d{4}-\d{2}-\d{2})/);
    if (match?.[1]) return normalizeToken(match[1]);

    const parsed = new Date(raw);
    if (!Number.isNaN(parsed.getTime())) {
      return normalizeToken(parsed.toISOString().slice(0, 10));
    }

    return normalizeToken(raw);
  };

  const invoiceDateKey = normalizeInvoiceDateKey(invoiceDateRaw);

  // Prefer stable keys that match across optimistic (local) rows and API rows.
  // Local rows typically won't have `invoice_token` yet, and date formats can differ,
  // so avoid using invoice date when an invoice number exists.
  if (invoiceNumberKey) {
    return `inv:${invoiceNumberKey}|po:${poNumberKey || "-"}`;
  }

  if (poNumberKey) {
    return `po:${poNumberKey}|dt:${invoiceDateKey || "-"}`;
  }

  return `invoice-row-${index}`;
};

const mergeInvoiceRows = (localInvoices, apiInvoices) => {
  const byKey = new Map();
  const orderedKeys = [];

  const upsert = (row, index) => {
    if (!row || typeof row !== "object") return;
    const baseKey = getRowKey(row, index);
    const tokenKey = getRowTokenKey(row);

    if (!byKey.has(baseKey)) {
      byKey.set(baseKey, row);
      orderedKeys.push(baseKey);
      return;
    }

    const existing = byKey.get(baseKey);
    const existingTokenKey = getRowTokenKey(existing);

    if (tokenKey && existingTokenKey && tokenKey !== existingTokenKey) {
      const uniqueKey = `${baseKey}|t:${tokenKey}`;
      if (!byKey.has(uniqueKey)) {
        byKey.set(uniqueKey, row);
        orderedKeys.push(uniqueKey);
        return;
      }

      const current = byKey.get(uniqueKey);
      byKey.set(uniqueKey, { ...(current || {}), ...(row || {}) });
      return;
    }

    // Prefer later data (typically API) while preserving any local-only fields.
    byKey.set(baseKey, { ...(existing || {}), ...(row || {}) });
  };

  (localInvoices || []).forEach((row, index) => upsert(row, index));
  (apiInvoices || []).forEach((row, index) =>
    upsert(row, (localInvoices?.length || 0) + index),
  );

  return orderedKeys.map((key) => byKey.get(key)).filter(Boolean);
};

const getApprovalStepForDesignation = (row, designation) => {
  if (!row || !designation) return null;
  const flow = toArray(row?.approval_flow);
  const normalizedDesignation = safeLower(designation);
  if (!normalizedDesignation) return null;

  return (
    flow.find(
      (step) => safeLower(step?.designation) === normalizedDesignation,
    ) || null
  );
};

const statusConfig = {
  matched: {
    label: "Matched",
    type: "matched",
    color: "#166534",
    bg: "#dcfce7",
    border: "#bbf7d0",
    icon: <CheckCircleIcon sx={{ fontSize: 15 }} />,
  },
  partial: {
    label: "Partial",
    type: "partial",
    color: "#b45309",
    bg: "#fef3c7",
    border: "#fde68a",
    icon: <WarningAmberIcon sx={{ fontSize: 15 }} />,
  },
  rejected: {
    label: "Exception Found",
    type: "rejected",
    color: "#b91c1c",
    bg: "#fee2e2",
    border: "#fecaca",
    icon: <ErrorOutlineIcon sx={{ fontSize: 15 }} />,
  },
  default: {
    label: "Not Compared",
    type: "default",
    color: "#475569",
    bg: "#f1f5f9",
    border: "#e2e8f0",
    icon: <ReceiptLongIcon sx={{ fontSize: 15 }} />,
  },
};

const getStatusFromText = (statusText) => {
  const normalized = safeLower(statusText);

  if (!normalized) return statusConfig.default;
  if (normalized === "matched" || normalized === "match") {
    return statusConfig.matched;
  }
  if (
    normalized.includes("mismatch") ||
    normalized.includes("fail") ||
    normalized.includes("not matched") ||
    normalized.includes("not_matched") ||
    normalized.includes("notmatched") ||
    normalized.includes("unmatched")
  ) {
    return statusConfig.rejected;
  }
  if (normalized.includes("partial") || normalized.includes("review")) {
    return statusConfig.partial;
  }
  if (normalized.includes("matched") && !normalized.includes("mismatch")) {
    return statusConfig.matched;
  }

  return {
    ...statusConfig.default,
    label: valueOrDash(statusText),
  };
};

const getCheckStatus = (check) =>
  getStatusFromText(pick(check, ["status", "match_status", "result"]));

const getOverallComparisonStatus = (row) => {
  const summary = getVerificationSummary(row);
  const checks = COMPARISON_CHECKS.map(({ key }) => summary?.[key]).filter(
    Boolean,
  );

  if (checks.length) {
    const matchedCount = checks.filter(
      (check) => getCheckStatus(check).type === "matched",
    ).length;

    if (matchedCount === checks.length) return statusConfig.matched;
    if (matchedCount > 0) {
      return {
        ...statusConfig.partial,
        label: "Partial Match",
      };
    }
    return statusConfig.rejected;
  }

  const rowStatus = pick(row, ["verification_status", "match_status"]);
  if (rowStatus) return getStatusFromText(rowStatus);

  return statusConfig.default;
};

const buildReconciliationSummary = (invoices = []) => {
  const summary = {
    total: 0,
    totalAmount: 0,
    matched: 0,
    partial: 0,
    mismatched: 0,
    unverified: 0,
  };

  if (!Array.isArray(invoices) || invoices.length === 0) return summary;

  invoices.forEach((row) => {
    summary.total += 1;

    const invoiceTotal = parseNumber(getInvoiceTotal(row));
    if (invoiceTotal != null) summary.totalAmount += invoiceTotal;

    const status = getOverallComparisonStatus(row);
    if (status?.type === "matched") summary.matched += 1;
    else if (status?.type === "partial") summary.partial += 1;
    else if (status?.type === "rejected") summary.mismatched += 1;
    else summary.unverified += 1;
  });

  return summary;
};

const StatusChip = ({ status }) => {
  const config = status || statusConfig.default;

  return (
    <Chip
      icon={config.icon}
      label={config.label}
      size="small"
      sx={{
        height: 30,
        borderRadius: "999px",
        color: config.color,
        backgroundColor: config.bg,
        border: `1px solid ${config.border}`,
        fontFamily: "gilroyMedium",
        fontSize: "14px",
        fontWeight: 700,
        "& .MuiChip-icon": {
          color: config.color,
          ml: 1,
        },
      }}
    />
  );
};

const SummaryCard = ({ label, value, highlight }) => (
  <Box
    sx={{
      flex: "1 1 180px",
      minWidth: 170,
      p: 1.6,
      borderRadius: 2,
      border: "1px solid #e2e8f0",
      background: highlight ? "#eff6ff" : "#ffffff",
    }}
  >
    <Typography
      sx={{
        fontFamily: "gilroyMedium",
        fontSize: "12px",
        color: "#64748b",
        mb: 0.6,
      }}
    >
      {label}
    </Typography>
    <Typography
      title={safeString(value)}
      sx={{
        fontFamily: "GilroyBold",
        fontSize: "15px",
        color: highlight ? "#075b97" : "#0f172a",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {valueOrDash(value)}
    </Typography>
  </Box>
);

const getDefaultCheckDetail = ({
  label,
  status,
  invoiceValue,
  poValue,
  mismatches = [],
}) => {
  if (mismatches.length) {
    return `${mismatches.length} ${label.toLowerCase()} issue(s) found. Review the line item comparison.`;
  }

  if (status.type === "matched") {
    return `${label} matched between the uploaded invoice and PO.`;
  }

  if (poValue === "-") {
    return `PO ${label.toLowerCase()} details were not returned in this invoice response.`;
  }

  return `Invoice value ${valueOrDash(
    invoiceValue,
  )}; PO value ${valueOrDash(poValue)}.`;
};

const getCheckComparisonRows = (row) => {
  const summary = getVerificationSummary(row);

  if (!Object.keys(summary || {}).length) {
    return [
      {
        label: "PO ID",
        status: statusConfig.default,
        invoiceValue: getPoNumber(row),
        poValue: "-",
        detail:
          "PO comparison summary was not returned by the invoice list API.",
      },
      {
        label: "Invoice Total",
        status: statusConfig.default,
        invoiceValue: formatCurrency(getInvoiceTotal(row), getCurrency(row)),
        poValue: "-",
        detail:
          "Only uploaded invoice values are available for this saved invoice.",
      },
    ];
  }

  return COMPARISON_CHECKS.map(({ key, label }) => {
    const check = summary?.[key] || {};
    const status = getCheckStatus(check);

    if (key === "item_count_match") {
      const invoiceValue = valueOrDash(check.invoice_item_count);
      const poValue = valueOrDash(check.po_item_count);
      return {
        label,
        status,
        invoiceValue,
        poValue,
        detail:
          check.message ||
          check.reason ||
          getDefaultCheckDetail({ label, status, invoiceValue, poValue }),
      };
    }

    if (key === "total_amount_match") {
      const invoiceValue = formatCurrency(
        check.invoice_total_amount ?? getInvoiceTotal(row),
        getCurrency(row),
      );
      const poValue = formatCurrency(check.po_total_amount, getCurrency(row));
      return {
        label,
        status,
        invoiceValue,
        poValue,
        detail:
          check.difference !== undefined && check.difference !== null
            ? `Difference: ${formatCurrency(check.difference, getCurrency(row))}`
            : check.message ||
              check.reason ||
              getDefaultCheckDetail({ label, status, invoiceValue, poValue }),
      };
    }

    if (key === "po_number_match") {
      const invoiceValue = valueOrDash(
        check.invoice_po_number ||
          check.invoice_pi_po_number ||
          getPoNumber(row),
      );
      const poValue = valueOrDash(check.po_number || getPoNumber(row));
      return {
        label,
        status,
        invoiceValue,
        poValue,
        detail:
          check.message ||
          check.reason ||
          getDefaultCheckDetail({ label, status, invoiceValue, poValue }),
      };
    }

    const mismatches = toArray(check.mismatched_items);
    const matches = toArray(check.matched_items);
    const mismatchCount = mismatches.length;
    const matchCount = matches.length;

    const defaultInvoiceValue =
      mismatchCount > 0
        ? `${mismatchCount} mismatch${mismatchCount > 1 ? "es" : ""}`
        : status.type === "matched"
          ? "Matched"
          : "-";
    const defaultPoValue =
      mismatchCount > 0
        ? `${mismatchCount} mismatch${mismatchCount > 1 ? "es" : ""}`
        : status.type === "matched"
          ? "Matched"
          : "-";

    const sampleDetails = (() => {
      if (!mismatchCount) return null;

      if (key === "item_name_match") {
        return mismatches
          .slice(0, 2)
          .map(
            (item) =>
              `${valueOrDash(item.invoice_item_code || item.item_code)}: Invoice "${valueOrDash(
                item.invoice_item_name || item.item_name,
              )}" vs PO "${valueOrDash(item.po_item_name)}"`,
          )
          .join(" | ");
      }

      if (key === "description_match") {
        return mismatches
          .slice(0, 2)
          .map((item) => {
            const code = valueOrDash(item.item_code || item.invoice_item_code);
            const score =
              item.description_match_score !== undefined &&
              item.description_match_score !== null
                ? `score ${item.description_match_score}`
                : null;
            const unmatched = toArray(item.description_unmatched_tokens);
            const unmatchedSummary = unmatched.length
              ? `unmatched: ${unmatched.slice(0, 4).join(", ")}${unmatched.length > 4 ? "…" : ""}`
              : null;
            return `${code}${score ? ` (${score})` : ""}${
              unmatchedSummary ? ` — ${unmatchedSummary}` : ""
            }`;
          })
          .join(" | ");
      }

      if (key === "quantity_match") {
        return mismatches
          .slice(0, 2)
          .map(
            (item) =>
              `${valueOrDash(item.invoice_item_code || item.item_code)}: Invoice qty ${valueOrDash(
                item.invoice_quantity,
              )} vs PO qty ${valueOrDash(item.po_quantity)}`,
          )
          .join(" | ");
      }

      if (key === "item_amount_match") {
        return mismatches
          .slice(0, 2)
          .map((item) => {
            const code = valueOrDash(item.invoice_item_code || item.item_code);
            const diff =
              item.amount_difference !== undefined &&
              item.amount_difference !== null
                ? `diff ${item.amount_difference}`
                : null;
            return `${code}: Invoice ${valueOrDash(
              item.invoice_total_amount,
            )} vs PO ${valueOrDash(item.po_total_amount)}${diff ? ` (${diff})` : ""}`;
          })
          .join(" | ");
      }

      return null;
    })();

    const invoiceValue =
      mismatchCount > 0 && matchCount > 0
        ? `${matchCount} matched, ${mismatchCount} mismatched`
        : defaultInvoiceValue;
    const poValue = invoiceValue;

    return {
      label,
      status,
      invoiceValue,
      poValue,
      detail:
        check.message ||
        check.reason ||
        sampleDetails ||
        getDefaultCheckDetail({
          label,
          status,
          invoiceValue,
          poValue,
          mismatches,
        }),
    };
  });
};

const getItemCode = (item) =>
  pick(item, [
    "item_code",
    "invoice_item_code",
    "po_item_code",
    "invoice_part_number",
    "po_part_number",
    "part_number",
    "part_no",
    "code",
  ]);

const getItemPartNumber = (item) =>
  pick(item, [
    "part_number",
    "part_no",
    "invoice_part_number",
    "po_part_number",
  ]);

const getItemName = (item) =>
  pick(item, [
    "invoice_item_name",
    "po_item_name",
    "item_name",
    "name",
    "product_name",
    "title",
  ]);

const getComparableItemName = (item) => {
  const name = getItemName(item);
  if (name !== undefined && name !== null && String(name).trim() !== "") {
    return name;
  }

  // Some invoice payloads place the item name in `description` when `item_name` is null.
  // Use it as a *name-like* fallback only when it's reasonably short.
  const desc = pick(item, [
    "description",
    "item_description",
    "description_of_goods",
  ]);
  if (!desc) return undefined;
  const text = String(desc).trim();
  if (!text) return undefined;
  if (text.length <= 80) return text;
  return undefined;
};

const getItemDescription = (item) =>
  pick(item, [
    "description",
    "item_description",
    "description_of_goods",
    "item_name",
    "name",
  ]);

const getItemQuantity = (item) =>
  pick(item, [
    "quantity",
    "qty",
    "invoice_quantity",
    "po_quantity",
    "order_quantity",
    "parsed_quantity",
  ]);

const getItemRate = (item) =>
  pick(item, ["unit_price", "unit_rate", "rate", "price", "parsed_rate"]);

const getItemTotal = (item) => {
  const explicitTotal = pick(item, [
    "total_amount",
    "line_total",
    "amount",
    "total",
    "invoice_total_amount",
    "po_total_amount",
  ]);
  if (explicitTotal !== undefined) return explicitTotal;

  const qty = parseNumber(getItemQuantity(item));
  const rate = parseNumber(getItemRate(item));
  if (qty === null || rate === null) return undefined;

  return qty * rate;
};

const collectMismatchItems = (row) => {
  const summary = getVerificationSummary(row);

  return COMPARISON_CHECKS.flatMap(({ key }) =>
    toArray(summary?.[key]?.mismatched_items).map((item) => ({
      ...item,
      checkKey: key,
      checkLabel: CHECK_LABEL_BY_KEY[key] || "Comparison",
    })),
  );
};

const getPreferredInvoiceIdentifier = (item) =>
  (() => {
    const part = normalizeToken(getItemPartNumber(item));
    const code = normalizeToken(getItemCode(item));
    const name = normalizeToken(
      getComparableItemName(item) || getItemName(item),
    );
    const description = normalizeToken(getItemDescription(item));

    // Prefer a composite key when item codes repeat across lines.
    if (code && name) return `${code}|${name}`;
    if (part) return part;
    if (code && description) return `${code}|${description}`;
    return part || code || name || description;
  })();

const getMatchKeysForIndex = (item) => {
  if (!item || typeof item !== "object") return [];
  const part = normalizeToken(getItemPartNumber(item));
  const code = normalizeToken(getItemCode(item));
  const name = normalizeToken(getComparableItemName(item) || getItemName(item));
  const description = normalizeToken(getItemDescription(item));
  const keys = [];

  if (code && name) keys.push(`${code}|${name}`);
  if (part) keys.push(part);
  if (code) keys.push(code);
  if (name) keys.push(name);
  if (code && description) keys.push(`${code}|${description}`);
  if (description) keys.push(description);

  return keys.filter(Boolean);
};

const buildMatchedItemIndex = (row) => {
  const summary = getVerificationSummary(row);
  const quantityMatches = toArray(summary?.quantity_match?.matched_items);
  const amountMatches = toArray(summary?.item_amount_match?.matched_items);
  // Prefer amount rows since they carry `po_total_amount` / `invoice_total_amount`.
  const merged = [...amountMatches, ...quantityMatches];

  const index = new Map();

  merged.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;

    const keys = [
      entry.invoice_part_number,
      entry.po_part_number,
      entry.invoice_item_code,
      entry.po_item_code,
      entry.item_code,
      entry.invoice_item_name,
      entry.po_item_name,
      entry.item_name,
    ]
      .map(normalizeToken)
      .filter(Boolean);

    // Composite keys to avoid collisions when codes repeat.
    const invoiceCode = normalizeToken(
      entry.invoice_item_code || entry.item_code,
    );
    const invoiceName = normalizeToken(entry.invoice_item_name);
    if (invoiceCode && invoiceName) keys.push(`${invoiceCode}|${invoiceName}`);

    keys.forEach((key) => {
      const existing = index.get(key);
      if (!existing) {
        index.set(key, entry);
        return;
      }

      const existingHasAmounts =
        existing.po_total_amount !== undefined ||
        existing.invoice_total_amount !== undefined;
      const entryHasAmounts =
        entry.po_total_amount !== undefined ||
        entry.invoice_total_amount !== undefined;

      if (!existingHasAmounts && entryHasAmounts) {
        index.set(key, entry);
      }
    });
  });

  return index;
};

const buildCheckItemIndex = (row, checkKey, mismatchFlagKey) => {
  const summary = getVerificationSummary(row);
  const checkSummary = summary?.[checkKey];
  const matchedItems = toArray(checkSummary?.matched_items);
  const mismatchedItems = toArray(checkSummary?.mismatched_items);
  const merged = [...matchedItems, ...mismatchedItems];

  const index = new Map();

  merged.forEach((entry) => {
    if (!entry || typeof entry !== "object") return;

    const keys = [
      entry.invoice_part_number,
      entry.po_part_number,
      entry.invoice_item_code,
      entry.po_item_code,
      entry.item_code,
      entry.invoice_item_name,
      entry.po_item_name,
      entry.item_name,
    ]
      .map(normalizeToken)
      .filter(Boolean);

    const invoiceCode = normalizeToken(
      entry.invoice_item_code || entry.item_code,
    );
    const invoiceName = normalizeToken(entry.invoice_item_name);
    if (invoiceCode && invoiceName) keys.push(`${invoiceCode}|${invoiceName}`);

    keys.forEach((key) => {
      const existing = index.get(key);
      if (!existing) {
        index.set(key, entry);
        return;
      }

      const entryMismatch =
        mismatchFlagKey && entry[mismatchFlagKey] === false ? 1 : 0;
      const existingMismatch =
        mismatchFlagKey && existing[mismatchFlagKey] === false ? 1 : 0;

      if (entryMismatch > existingMismatch) {
        index.set(key, entry);
      }
    });
  });

  return {
    index,
    minimumScore: checkSummary?.minimum_match_score,
  };
};

const buildItemNameMatchIndex = (row) => {
  return buildCheckItemIndex(row, "item_name_match", "item_name_match").index;
};

const getItemNameStatus = ({
  hasPoItem,
  invoiceItem,
  poItem,
  matchedEntry,
  nameMatchEntry,
}) => {
  if (!hasPoItem) {
    return {
      ...statusConfig.default,
      label: "Invoice Only",
    };
  }

  if (nameMatchEntry && typeof nameMatchEntry === "object") {
    const explicit = nameMatchEntry.item_name_match;
    if (explicit === true) return statusConfig.matched;
    if (explicit === false) {
      return {
        ...statusConfig.rejected,
        label: "Unmatched",
      };
    }
  }

  const invoiceNameRaw =
    invoiceItem?.invoice_item_name ||
    getItemName(invoiceItem) ||
    matchedEntry?.invoice_item_name ||
    matchedEntry?.item_name;
  const poNameRaw =
    poItem?.po_item_name || getItemName(poItem) || matchedEntry?.po_item_name;
  const invoiceName = normalizeToken(invoiceNameRaw);
  const poName = normalizeToken(poNameRaw);

  if (invoiceName && poName) {
    if (invoiceName === poName) return statusConfig.matched;
    return {
      ...statusConfig.rejected,
      label: "Unmatched",
    };
  }

  return statusConfig.default;
};

const resolveItemNames = ({
  invoiceItem,
  poItem,
  quantityEntry,
  amountEntry,
  nameEntry,
  descriptionEntry,
}) => {
  const invoiceName =
    nameEntry?.invoice_item_name ||
    amountEntry?.invoice_item_name ||
    quantityEntry?.invoice_item_name ||
    descriptionEntry?.invoice_item_name ||
    invoiceItem?.invoice_item_name ||
    getComparableItemName(invoiceItem) ||
    getItemName(invoiceItem) ||
    "-";

  const poName =
    nameEntry?.po_item_name ||
    amountEntry?.po_item_name ||
    quantityEntry?.po_item_name ||
    descriptionEntry?.po_item_name ||
    poItem?.po_item_name ||
    getComparableItemName(poItem) ||
    getItemName(poItem) ||
    "-";

  return {
    invoiceName: valueOrDash(invoiceName),
    poName: valueOrDash(poName),
  };
};

const resolveItemDescriptions = ({
  invoiceItem,
  poItem,
  mismatch,
  descriptionEntry,
}) => {
  const invoiceDescription =
    descriptionEntry?.invoice_description ||
    invoiceItem?.invoice_description ||
    pick(invoiceItem, [
      "description",
      "item_description",
      "description_of_goods",
    ]) ||
    mismatch?.invoice_description ||
    mismatch?.description ||
    "-";

  const poDescription =
    descriptionEntry?.po_description ||
    poItem?.po_description ||
    pick(poItem, ["description", "item_description", "description_of_goods"]) ||
    mismatch?.po_description ||
    "-";

  return {
    invoiceDescription: valueOrDash(invoiceDescription),
    poDescription: valueOrDash(poDescription),
  };
};

const resolveUnitRates = ({
  invoiceItem,
  poItem,
  mismatch,
  invoiceQty,
  poQty,
  invoiceTotal,
  poTotal,
}) => {
  const rawInvoiceRate =
    getItemRate(invoiceItem) ??
    pick(mismatch, [
      "invoice_unit_price",
      "invoice_rate",
      "unit_price",
      "rate",
      "price",
    ]);
  const rawPoRate =
    getItemRate(poItem) ??
    pick(mismatch, ["po_unit_price", "po_rate", "unit_price", "rate", "price"]);

  const invoiceRate = rawInvoiceRate ?? "-";
  const poRate = rawPoRate ?? "-";

  const invRateNumber = parseNumber(invoiceRate);
  const poRateNumber = parseNumber(poRate);

  const invQtyNumber = parseNumber(invoiceQty);
  const poQtyNumber = parseNumber(poQty);
  const invTotalNumber = parseNumber(invoiceTotal);
  const poTotalNumber = parseNumber(poTotal);

  const computedInvoiceRate =
    invRateNumber !== null
      ? invoiceRate
      : invQtyNumber && invTotalNumber !== null
        ? invTotalNumber / invQtyNumber
        : invoiceRate;
  const computedPoRate =
    poRateNumber !== null
      ? poRate
      : poQtyNumber && poTotalNumber !== null
        ? poTotalNumber / poQtyNumber
        : poRate;

  return {
    invoiceRate: computedInvoiceRate,
    poRate: computedPoRate,
  };
};

const formatMismatchDetail = (parts) =>
  parts
    .filter(Boolean)
    .map((part) => part.trim())
    .filter(Boolean)
    .join(" | ");

const buildLineIssues = ({
  hasPoItem,
  invoiceQty,
  poQty,
  invoiceRate,
  poRate,
  invoiceTotal,
  poTotal,
  invoiceName,
  poName,
  quantityEntry,
  amountEntry,
  nameEntry,
  descriptionEntry,
}) => {
  if (!hasPoItem) {
    return {
      overallStatus: { ...statusConfig.default, label: "Invoice Only" },
      detail: "No matching PO line item was found for this invoice line.",
      issueKeys: ["po_missing"],
      mismatchFlags: {
        name: false,
        description: false,
        qty: false,
        unit_price: false,
        total: false,
        po_missing: true,
      },
    };
  }

  const issues = [];
  const issueKeys = new Set();
  const mismatchFlags = {
    name: false,
    description: false,
    qty: false,
    unit_price: false,
    total: false,
    po_missing: false,
  };

  if (nameEntry && nameEntry.item_name_match === false) {
    const score =
      nameEntry.item_name_match_score !== undefined &&
      nameEntry.item_name_match_score !== null
        ? `Score ${nameEntry.item_name_match_score}`
        : null;
    const algorithm = nameEntry.item_name_match_algorithm
      ? `Rule ${nameEntry.item_name_match_algorithm}`
      : null;
    issues.push(
      formatMismatchDetail([
        `Item name mismatch (Invoice: ${valueOrDash(invoiceName)}; PO: ${valueOrDash(poName)})`,
        score,
        algorithm,
      ]),
    );
    issueKeys.add("name");
    mismatchFlags.name = true;
  }

  if (descriptionEntry && descriptionEntry.description_match === false) {
    const score =
      descriptionEntry.description_match_score !== undefined &&
      descriptionEntry.description_match_score !== null
        ? `Score ${descriptionEntry.description_match_score}`
        : null;
    const unmatchedTokens = toArray(
      descriptionEntry.description_unmatched_tokens,
    );
    const tokensSummary = unmatchedTokens.length
      ? `Unmatched: ${unmatchedTokens.slice(0, 6).join(", ")}${
          unmatchedTokens.length > 6 ? "…" : ""
        }`
      : null;
    issues.push(
      formatMismatchDetail(["Description mismatch", score, tokensSummary]),
    );
    issueKeys.add("description");
    mismatchFlags.description = true;
  }

  const invQtyNumber = parseNumber(invoiceQty);
  const poQtyNumber = parseNumber(poQty);
  const qtyMismatch =
    (quantityEntry &&
      quantityEntry.difference &&
      Number(quantityEntry.difference) !== 0) ||
    (invQtyNumber !== null &&
      poQtyNumber !== null &&
      invQtyNumber !== poQtyNumber);
  if (qtyMismatch) {
    issues.push(
      `Quantity mismatch (Invoice: ${valueOrDash(invoiceQty)}; PO: ${valueOrDash(poQty)})`,
    );
    issueKeys.add("qty");
    mismatchFlags.qty = true;
  }

  const invRateNumber = parseNumber(invoiceRate);
  const poRateNumber = parseNumber(poRate);
  if (
    invRateNumber !== null &&
    poRateNumber !== null &&
    invRateNumber !== poRateNumber
  ) {
    issues.push(
      `Unit price mismatch (Invoice: ${valueOrDash(invoiceRate)}; PO: ${valueOrDash(poRate)})`,
    );
    issueKeys.add("unit_price");
    mismatchFlags.unit_price = true;
  }

  const invTotalNumber = parseNumber(invoiceTotal);
  const poTotalNumber = parseNumber(poTotal);
  const amountMismatch =
    (amountEntry && amountEntry.amount_match === false) ||
    (invTotalNumber !== null &&
      poTotalNumber !== null &&
      invTotalNumber !== poTotalNumber);
  if (amountMismatch) {
    const diff =
      amountEntry?.amount_difference ??
      (invTotalNumber !== null && poTotalNumber !== null
        ? invTotalNumber - poTotalNumber
        : null);
    issues.push(
      formatMismatchDetail([
        `Amount mismatch (Invoice: ${valueOrDash(invoiceTotal)}; PO: ${valueOrDash(poTotal)})`,
        diff !== null ? `Diff ${diff}` : null,
      ]),
    );
    issueKeys.add("total");
    mismatchFlags.total = true;
  }

  if (!issues.length) {
    return {
      overallStatus: statusConfig.matched,
      detail:
        "Matched across quantity, amount, description, and item name (as available).",
      issueKeys: [],
      mismatchFlags,
    };
  }

  return {
    overallStatus: { ...statusConfig.rejected, label: "Exception Found" },
    detail: issues.join(" | "),
    issueKeys: Array.from(issueKeys),
    mismatchFlags,
  };
};

const scoreDescriptionMatch = (a, b) => {
  const left = safeLower(a)
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
  const right = safeLower(b)
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  if (!left || !right) return 0;
  if (left === right) return 100;

  const leftTokens = new Set(left.split(/\s+/).filter(Boolean));
  const rightTokens = new Set(right.split(/\s+/).filter(Boolean));
  if (!leftTokens.size || !rightTokens.size) return 0;

  let overlap = 0;
  for (const token of leftTokens) {
    if (rightTokens.has(token)) overlap += 1;
  }

  const denom = Math.max(leftTokens.size, rightTokens.size);
  return Math.round((overlap / denom) * 100);
};

const findMatchingPoItemIndex = (invoiceItem, poItems) => {
  const invoicePart = normalizeToken(getItemPartNumber(invoiceItem));
  const invoiceCode = normalizeToken(getItemCode(invoiceItem));
  const invoiceName = normalizeToken(getItemName(invoiceItem));
  const invoiceDescriptionRaw = getItemDescription(invoiceItem);
  const invoiceDescription = normalizeToken(invoiceDescriptionRaw);

  let bestIndex = -1;
  let bestScore = -1;

  for (let index = 0; index < poItems.length; index += 1) {
    const poItem = poItems[index];
    const poPart = normalizeToken(getItemPartNumber(poItem));
    const poCode = normalizeToken(getItemCode(poItem));
    const poName = normalizeToken(getItemName(poItem));
    const poDescriptionRaw = getItemDescription(poItem);
    const poDescription = normalizeToken(poDescriptionRaw);

    if (invoicePart && poPart && invoicePart === poPart) {
      return index;
    }

    if (invoiceCode && poCode && invoiceCode === poCode) {
      return index;
    }

    if (invoiceName && poName && invoiceName === poName) {
      return index;
    }

    if (invoiceDescription && poDescription) {
      const score = scoreDescriptionMatch(
        invoiceDescriptionRaw ?? invoiceDescription,
        poDescriptionRaw ?? poDescription,
      );
      if (score > bestScore) {
        bestScore = score;
        bestIndex = index;
      }
    }
  }

  return bestScore >= 60 ? bestIndex : -1;
};

const findMismatchForItem = (invoiceItem, mismatchItems) => {
  const invoicePart = normalizeToken(getItemPartNumber(invoiceItem));
  const invoiceCode = normalizeToken(getItemCode(invoiceItem));
  const invoiceName = normalizeToken(getItemName(invoiceItem));
  const invoiceDescriptionRaw = getItemDescription(invoiceItem);
  const invoiceDescription = normalizeToken(invoiceDescriptionRaw);

  return mismatchItems.find((item) => {
    const mismatchPart = normalizeToken(getItemPartNumber(item));
    const mismatchCode = normalizeToken(getItemCode(item));
    const mismatchName = normalizeToken(getItemName(item));
    const mismatchDescriptionRaw = getItemDescription(item);
    const mismatchDescription = normalizeToken(mismatchDescriptionRaw);

    return (
      (invoicePart && mismatchPart && invoicePart === mismatchPart) ||
      (invoiceCode && mismatchCode && invoiceCode === mismatchCode) ||
      (invoiceName && mismatchName && invoiceName === mismatchName) ||
      (invoiceDescription &&
        mismatchDescription &&
        scoreDescriptionMatch(
          invoiceDescriptionRaw ?? invoiceDescription,
          mismatchDescriptionRaw ?? mismatchDescription,
        ) >= 60)
    );
  });
};

const getLineStatus = ({
  mismatch,
  hasPoItem,
  invoiceQty,
  poQty,
  invoiceTotal,
  poTotal,
}) => {
  if (mismatch) {
    return {
      ...statusConfig.rejected,
      label: `${mismatch.checkLabel} Mismatch`,
    };
  }

  if (!hasPoItem) {
    return {
      ...statusConfig.default,
      label: "Invoice Only",
    };
  }

  const invQty = parseNumber(invoiceQty);
  const purchaseQty = parseNumber(poQty);
  const invTotal = parseNumber(invoiceTotal);
  const purchaseTotal = parseNumber(poTotal);
  const quantityMatches =
    invQty !== null && purchaseQty !== null && invQty === purchaseQty;
  const amountMatches =
    invTotal !== null && purchaseTotal !== null && invTotal === purchaseTotal;

  if (quantityMatches && amountMatches) return statusConfig.matched;

  return {
    ...statusConfig.partial,
    label: "Review",
  };
};

const getLineDetail = ({ mismatch, hasPoItem, status }) => {
  if (mismatch) {
    return `${mismatch.checkLabel} mismatch returned by verification.`;
  }

  if (!hasPoItem) {
    return "PO line item was not returned in this invoice response.";
  }

  if (status.type === "matched") {
    return "Invoice quantity and amount match the PO line item.";
  }

  return "Invoice quantity or amount differs from the PO line item.";
};

const buildLineComparisonRows = (row) => {
  const invoiceItems = getInvoiceItems(row);
  const poItems = getPoItems(row);
  const mismatchItems = collectMismatchItems(row);
  const matchedIndex = buildMatchedItemIndex(row);
  const itemNameIndex = buildItemNameMatchIndex(row);
  const quantityIndex = buildCheckItemIndex(row, "quantity_match", null).index;
  const amountIndex = buildCheckItemIndex(
    row,
    "item_amount_match",
    "amount_match",
  ).index;
  const descriptionIndex = buildCheckItemIndex(
    row,
    "description_match",
    "description_match",
  ).index;
  const remainingPoItems = [...poItems];

  const rowsFromInvoice = invoiceItems.map((invoiceItem, index) => {
    const matchIndex = findMatchingPoItemIndex(invoiceItem, remainingPoItems);
    const poItem = matchIndex >= 0 ? remainingPoItems[matchIndex] : null;
    if (matchIndex >= 0) {
      remainingPoItems.splice(matchIndex, 1);
    }
    const mismatch = findMismatchForItem(invoiceItem, mismatchItems);
    const keys = getMatchKeysForIndex(invoiceItem);
    const findInIndex = (map) => {
      for (const k of keys) {
        const hit = map.get(k);
        if (hit) return hit;
      }
      return null;
    };

    const matchedEntry = keys.length ? findInIndex(matchedIndex) : null;
    const nameMatchEntry = keys.length ? findInIndex(itemNameIndex) : null;
    const quantityEntry = keys.length ? findInIndex(quantityIndex) : null;
    const amountEntry = keys.length ? findInIndex(amountIndex) : null;
    const descriptionEntry = keys.length ? findInIndex(descriptionIndex) : null;
    const invoiceQty =
      getItemQuantity(invoiceItem) ?? mismatch?.invoice_quantity ?? "-";
    const poQty = getItemQuantity(poItem) ?? mismatch?.po_quantity ?? "-";
    const invoiceTotal =
      getItemTotal(invoiceItem) ?? mismatch?.invoice_total_amount ?? "-";
    const poTotal = getItemTotal(poItem) ?? mismatch?.po_total_amount ?? "-";
    const { invoiceRate, poRate } = resolveUnitRates({
      invoiceItem,
      poItem,
      mismatch,
      invoiceQty,
      poQty,
      invoiceTotal,
      poTotal,
    });

    const resolvedInvoiceQty =
      invoiceQty !== "-" && invoiceQty !== undefined && invoiceQty !== null
        ? invoiceQty
        : (matchedEntry?.invoice_quantity ?? invoiceQty);
    const resolvedPoQty =
      poQty !== "-" && poQty !== undefined && poQty !== null
        ? poQty
        : (matchedEntry?.po_quantity ?? poQty);
    const resolvedInvoiceTotal =
      invoiceTotal !== "-" &&
      invoiceTotal !== undefined &&
      invoiceTotal !== null
        ? invoiceTotal
        : (matchedEntry?.invoice_total_amount ?? invoiceTotal);
    const resolvedPoTotal =
      poTotal !== "-" && poTotal !== undefined && poTotal !== null
        ? poTotal
        : (matchedEntry?.po_total_amount ?? poTotal);
    const hasPoItem =
      Boolean(poItem) ||
      (matchedEntry &&
        (matchedEntry.po_quantity !== undefined ||
          matchedEntry.po_total_amount !== undefined ||
          matchedEntry.po_part_number ||
          matchedEntry.po_item_code));

    const { invoiceName, poName } = resolveItemNames({
      invoiceItem,
      poItem,
      quantityEntry,
      amountEntry,
      nameEntry: nameMatchEntry,
      descriptionEntry,
    });

    const lineIssueSummary = buildLineIssues({
      hasPoItem,
      invoiceQty: resolvedInvoiceQty,
      poQty: resolvedPoQty,
      invoiceRate,
      poRate,
      invoiceTotal: resolvedInvoiceTotal,
      poTotal: resolvedPoTotal,
      invoiceName,
      poName,
      quantityEntry,
      amountEntry,
      nameEntry: nameMatchEntry,
      descriptionEntry,
    });

    const { invoiceDescription, poDescription } = resolveItemDescriptions({
      invoiceItem,
      poItem,
      mismatch,
      descriptionEntry,
    });

    return {
      key: `${getItemCode(invoiceItem) || "invoice"}-${index}`,
      itemCode: valueOrDash(getItemCode(invoiceItem) || getItemCode(mismatch)),
      invoiceItemName: invoiceName,
      poItemName: poName,
      invoiceDescription,
      poDescription,
      invoiceQty: resolvedInvoiceQty,
      poQty: resolvedPoQty,
      invoiceRate,
      poRate,
      invoiceTotal: resolvedInvoiceTotal,
      poTotal: resolvedPoTotal,
      status: lineIssueSummary.overallStatus,
      detail: lineIssueSummary.detail,
      issueKeys: lineIssueSummary.issueKeys,
      mismatchFlags: lineIssueSummary.mismatchFlags,
    };
  });

  if (rowsFromInvoice.length) return rowsFromInvoice;

  return mismatchItems.map((item, index) => {
    const keys = getMatchKeysForIndex(item);
    const findInIndex = (map) => {
      for (const k of keys) {
        const hit = map.get(k);
        if (hit) return hit;
      }
      return null;
    };

    const matchedEntry = keys.length ? findInIndex(matchedIndex) : null;
    const nameMatchEntry = keys.length ? findInIndex(itemNameIndex) : null;
    const quantityEntry = keys.length ? findInIndex(quantityIndex) : null;
    const amountEntry = keys.length ? findInIndex(amountIndex) : null;
    const descriptionEntry = keys.length ? findInIndex(descriptionIndex) : null;
    const hasPoItem =
      item.po_quantity !== undefined ||
      item.po_total_amount !== undefined ||
      Boolean(item.po_item_name) ||
      Boolean(matchedEntry?.po_quantity) ||
      Boolean(matchedEntry?.po_total_amount) ||
      Boolean(matchedEntry?.po_item_name);
    const { invoiceName, poName } = resolveItemNames({
      invoiceItem: item,
      poItem: item,
      quantityEntry,
      amountEntry,
      nameEntry: nameMatchEntry,
      descriptionEntry,
    });
    const { invoiceDescription, poDescription } = resolveItemDescriptions({
      invoiceItem: item,
      poItem: item,
      mismatch: item,
      descriptionEntry,
    });

    const { invoiceRate, poRate } = resolveUnitRates({
      invoiceItem: item,
      poItem: item,
      mismatch: item,
      invoiceQty: item.invoice_quantity ?? matchedEntry?.invoice_quantity,
      poQty: item.po_quantity ?? matchedEntry?.po_quantity,
      invoiceTotal:
        item.invoice_total_amount ?? matchedEntry?.invoice_total_amount,
      poTotal: item.po_total_amount ?? matchedEntry?.po_total_amount,
    });

    const lineIssueSummary = buildLineIssues({
      hasPoItem,
      invoiceQty: valueOrDash(
        item.invoice_quantity ?? matchedEntry?.invoice_quantity,
      ),
      poQty: valueOrDash(item.po_quantity ?? matchedEntry?.po_quantity),
      invoiceRate: valueOrDash(invoiceRate),
      poRate: valueOrDash(poRate),
      invoiceTotal: valueOrDash(
        item.invoice_total_amount ?? matchedEntry?.invoice_total_amount,
      ),
      poTotal: valueOrDash(
        item.po_total_amount ?? matchedEntry?.po_total_amount,
      ),
      invoiceName,
      poName,
      quantityEntry,
      amountEntry,
      nameEntry: nameMatchEntry,
      descriptionEntry,
    });

    return {
      key: `mismatch-${index}`,
      itemCode: valueOrDash(getItemCode(item)),
      invoiceItemName: invoiceName,
      poItemName: poName,
      invoiceDescription,
      poDescription,
      invoiceQty: valueOrDash(
        item.invoice_quantity ?? matchedEntry?.invoice_quantity,
      ),
      poQty: valueOrDash(item.po_quantity ?? matchedEntry?.po_quantity),
      invoiceRate: valueOrDash(invoiceRate),
      poRate: valueOrDash(poRate),
      invoiceTotal: valueOrDash(
        item.invoice_total_amount ?? matchedEntry?.invoice_total_amount,
      ),
      poTotal: valueOrDash(
        item.po_total_amount ?? matchedEntry?.po_total_amount,
      ),
      status: lineIssueSummary.overallStatus,
      detail: lineIssueSummary.detail,
      issueKeys: lineIssueSummary.issueKeys,
      mismatchFlags: lineIssueSummary.mismatchFlags,
    };
  });
};

const InvoiceTable = ({
  data: localInvoices = [],
  VendorToken,
  BuyerToken,
  OrgToken,
  AuthToken,
  mode = "vendor", // "vendor" | "approver" | "buyer"
  ApproverToken,
  ApproverDesignation,
  queueStatus = "all",
  onSummaryChange,
  refreshToken = 0,
}) => {
  const [data, setData] = useState([]);
  const [apiTotalCount, setApiTotalCount] = useState(0);
  const [serverRowsPerPage, setServerRowsPerPage] = useState(ROWS_PER_PAGE);
  const [paginationData, setPaginationData] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [openLineDrawer, setOpenLineDrawer] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const isApproverMode = mode === "approver";
  const isBuyerMode = mode === "buyer";
  const pageStorageKey = isBuyerMode
    ? "buyer_invoice_reconciliation_page_number"
    : "vendor_invoice_reconciliation_page_number";
  const [pageNumber, setPageNumber] = useState(() => {
    try {
      const stored = localStorage.getItem(pageStorageKey);
      const n = parseInt(stored || "", 10);
      return Number.isFinite(n) && n > 0 ? n : 1;
    } catch {
      return 1;
    }
  });
  const [loading, setLoading] = useState(true);
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [approvalActionLoading, setApprovalActionLoading] = useState(false);
  const [approvalActionError, setApprovalActionError] = useState("");
  const [approvalActionSuccess, setApprovalActionSuccess] = useState("");

  const lastFetchKeyRef = useRef("");
  const stickyPoColumnWidth = 148;
  const stickyActionColumnWidth = 76;

  const vendorPageIndex = useMemo(() => {
    if (isApproverMode) return page;
    const n = parseInt(String(pageNumber || ""), 10);
    return Number.isFinite(n) && n > 0 ? n - 1 : 0;
  }, [isApproverMode, page, pageNumber]);

  const normalizeApprovalLabel = (row) => {
    const labelFromApi = pick(row, ["approval_status_label"]);
    if (labelFromApi) return String(labelFromApi);

    const status = pick(row, ["approval_status"]);
    if (!status) return "";
    const raw = String(status);
    return raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : "";
  };

  const getApprovalStatusMeta = (row) => {
    const currentApproverStep =
      isApproverMode && ApproverDesignation
        ? getApprovalStepForDesignation(row, ApproverDesignation)
        : null;
    const currentApproverStatus = safeLower(currentApproverStep?.status);

    if (currentApproverStatus === "approved") {
      return {
        value: "Approved",
        color: "#166534",
        bg: "#dcfce7",
        border: "#bbf7d0",
      };
    }

    if (currentApproverStatus === "rejected") {
      return {
        value: "Rejected",
        color: "#b91c1c",
        bg: "#fee2e2",
        border: "#fecaca",
      };
    }

    const label = normalizeApprovalLabel(row);
    const normalized = safeLower(label);
    const status = safeLower(pick(row, ["approval_status"]));

    const value =
      label ||
      (status ? status.charAt(0).toUpperCase() + status.slice(1) : "-");

    if (normalized.includes("approve") || status === "approved") {
      return {
        value: value || "Approved",
        color: "#166534",
        bg: "#dcfce7",
        border: "#bbf7d0",
      };
    }
    if (normalized.includes("reject") || status === "rejected") {
      return {
        value: value || "Rejected",
        color: "#b91c1c",
        bg: "#fee2e2",
        border: "#fecaca",
      };
    }
    if (normalized.includes("wait") || status === "waiting") {
      return {
        value: value || "Waiting",
        color: "#1d4ed8",
        bg: "#dbeafe",
        border: "#bfdbfe",
      };
    }
    if (normalized.includes("pending") || status === "pending") {
      return {
        value: value || "Awaiting",
        color: "#b45309",
        bg: "#fef3c7",
        border: "#fde68a",
      };
    }

    if (!value || value === "-") {
      const comparisonStatus = getOverallComparisonStatus(row);
      if (comparisonStatus?.type === "matched") {
        return {
          value: "Pending",
          color: "#b45309",
          bg: "#fef3c7",
          border: "#fde68a",
        };
      }
    }

    return {
      value: valueOrDash(value),
      color: "#475569",
      bg: "#f1f5f9",
      border: "#e2e8f0",
    };
  };

  const getBuyerApprovers = (row) => {
    const approvalFlow = toArray(
      row?.approval_flow ?? row?.approval_chain ?? row?.approvers,
    );

    const approvers = approvalFlow
      .map((step) =>
        safeString(
          step?.approved_by_name ??
            step?.approver_name ??
            step?.approved_by ??
            step?.designation,
        ).trim(),
      )
      .filter((name) => name && name !== "[object Object]");

    return approvers.join(", ") || "-";
  };

  const getApprovalTooltipContent = (row) => {
    const pendingWith = pick(row, ["pending_with"]);
    const flow = toArray(row?.approval_flow);
    const rejectedReason = pick(row, ["rejected_reason"]);
    const approvalLabel = normalizeApprovalLabel(row);
    const approvalLabelLower = safeLower(approvalLabel);
    const comparisonStatus = getOverallComparisonStatus(row);

    const getApproverName = (step) => {
      if (!step || typeof step !== "object") return "";

      const direct =
        step.approved_by_name ??
        step.approved_by_user_name ??
        step.approved_by_full_name ??
        step.approved_by_username ??
        step.Approver_name ??
        step.approver_name ??
        step.approver;

      if (direct !== undefined && direct !== null) {
        const trimmed = safeString(direct).trim();
        if (trimmed && trimmed !== "[object Object]") return trimmed;
      }

      const approvedBy = step.approved_by;
      if (typeof approvedBy === "string") {
        const trimmed = approvedBy.trim();
        if (trimmed) return trimmed;
      }

      if (approvedBy && typeof approvedBy === "object") {
        const nested =
          approvedBy.name ||
          approvedBy.full_name ||
          approvedBy.fullName ||
          approvedBy.username ||
          approvedBy.email;
        const trimmed = safeString(nested).trim();
        if (trimmed && trimmed !== "[object Object]") return trimmed;
      }

      const nestedObj =
        step.approved_by_user ||
        step.approver_user ||
        step.approved_by_details ||
        step.approver_details ||
        step.user;

      if (nestedObj && typeof nestedObj === "object") {
        const nested =
          nestedObj.name ||
          nestedObj.full_name ||
          nestedObj.fullName ||
          nestedObj.username ||
          nestedObj.email;
        const trimmed = safeString(nested).trim();
        if (trimmed && trimmed !== "[object Object]") return trimmed;
      }

      const fallback =
        step.approved_by_email ??
        step.approver_email ??
        step.email ??
        step.approved_by_mobile ??
        step.mobile_number;

      return safeString(fallback).trim();
    };

    const lines = [];
    if (
      approvalLabelLower.includes("not sent") &&
      comparisonStatus?.type === "partial"
    ) {
      lines.push(
        "Approval will be sent only when the PO and invoice match completely.",
      );
    }
    if (pendingWith) lines.push(`Pending with: ${pendingWith}`);
    if (rejectedReason) lines.push(`Reason: ${rejectedReason}`);

    if (flow.length) {
      flow.forEach((step) => {
        const level = step?.approval_level ?? "-";
        const designation = step?.designation ?? "-";
        const statusText = step?.status ? String(step.status) : "-";
        const approverName = getApproverName(step);
        const by = approverName ? ` (${approverName})` : "";
        const remarks = safeString(step?.remarks).trim();
        const at = step?.approved_at
          ? ` @ ${formatDate(step.approved_at)}`
          : "";
        lines.push(`L${level}: ${designation} - ${statusText}${by}${at}`);
        if (remarks) lines.push(`   Remarks: ${remarks}`);
      });
    }

    return lines.filter(Boolean).join("\n") || "—";
  };

  const vendorPage = isApproverMode ? 0 : vendorPageIndex;

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      const config = AuthToken
        ? { headers: { Authorization: `Bearer ${AuthToken}` } }
        : undefined;

      if (isApproverMode) {
        if (!ApproverToken) {
          setData([]);
          setApiTotalCount(0);
          if (typeof onSummaryChange === "function") {
            onSummaryChange(buildReconciliationSummary([]));
          }
          return [];
        }

        const res = await axios.post(
          API_ENDPOINT.get_vendor_invoice_document_approval_queue,
          { approver_token: ApproverToken, status: queueStatus || "all" },
          config,
        );
        const responseData = res?.data || {};
        const invoices = normalizeApiInvoices(responseData);
        setData(invoices);
        setApiTotalCount(
          getResponseCount(responseData) ?? Number(invoices.length || 0),
        );
        if (typeof onSummaryChange === "function") {
          onSummaryChange(buildReconciliationSummary(invoices));
        }
        return invoices;
      }

      if (isBuyerMode) {
        if (!BuyerToken) {
          setData([]);
          setApiTotalCount(0);
          setPaginationData(null);
          if (typeof onSummaryChange === "function") {
            onSummaryChange(buildReconciliationSummary([]));
          }
          return [];
        }

        const requestKey = `${String(BuyerToken || "")}|${String(
          AuthToken || "",
        )}|${vendorPage}|${ROWS_PER_PAGE}|${String(search || "").trim()}`;
        if (lastFetchKeyRef.current === requestKey) {
          return [];
        }
        lastFetchKeyRef.current = requestKey;

        const payload = {
          buyer_token: BuyerToken,
          page: vendorPage + 1,
          page_size: ROWS_PER_PAGE,
        };

        if (String(search || "").trim()) {
          payload.search = String(search || "").trim();
        }

        const res = await axios.post(
          API_ENDPOINT.get_buyer_invoice_reconciliation,
          payload,
          config,
        );

        const responseData = res?.data || {};
        const reconciliation =
          responseData?.response?.invoice_reconciliation || {};
        const summaryData = reconciliation?.summary || {};
        const statusCounts = reconciliation?.match_status_counts || {};
        const invoices = normalizeApiInvoices(responseData);
        const currentPage = Number(responseData?.current_page || vendorPage + 1);
        const totalCount =
          Number(summaryData?.total_invoice_count) ||
          getResponseCount(responseData) ||
          Number(invoices.length || 0);
        const totalPages =
          Number(responseData?.page_count) ||
          Number(responseData?.total_pages) ||
          Number(
            totalCount > 0 ? Math.ceil(totalCount / ROWS_PER_PAGE) : 0,
          );

        setData(invoices);
        setPaginationData({
          current_page: currentPage,
          page_size: Number(responseData?.page_size || ROWS_PER_PAGE),
          total_count: totalCount,
          total_pages: totalPages,
          has_next:
            responseData?.has_next ?? (totalPages > 0 && currentPage < totalPages),
          has_previous:
            responseData?.has_previous ?? currentPage > 1,
          current_page_count: totalCount,
          page_count: totalPages,
        });
        setApiTotalCount(totalCount);
        setServerRowsPerPage(Number(responseData?.page_size || ROWS_PER_PAGE));
        if (typeof onSummaryChange === "function") {
          onSummaryChange({
            total: totalCount,
            totalAmount: Number(summaryData?.total_invoice_amount || 0),
            matched: Number(statusCounts?.matched || 0),
            partial: Number(statusCounts?.partially_matched || 0),
            mismatched: Number(statusCounts?.mismatched || 0),
            unverified: Number(statusCounts?.unverified || 0),
          });
        }
        return invoices;
      }

      if (!VendorToken) {
        setData([]);
        setApiTotalCount(0);
        if (typeof onSummaryChange === "function") {
          onSummaryChange(buildReconciliationSummary([]));
        }
        return [];
      }

      const requestKey = `${String(VendorToken || "")}|${String(
        AuthToken || "",
      )}|${vendorPage}|${ROWS_PER_PAGE}|${refreshToken}`;
      if (lastFetchKeyRef.current === requestKey) {
        return [];
      }
      lastFetchKeyRef.current = requestKey;

      const res = await axios.post(
        API_ENDPOINT.get_vendor_invoices,
        { vendor_token: VendorToken, page: vendorPage + 1 },
        config,
      );

      const responseData = res?.data || {};
      const declaredPageSize = Number(responseData?.page_size || 0);
      const invoicesRaw = normalizeApiInvoices(responseData);
      const invoices =
        Number.isFinite(declaredPageSize) && declaredPageSize > 0
          ? invoicesRaw.slice(0, declaredPageSize)
          : invoicesRaw;

      setData(invoices);
      setPaginationData({
        current_page: Number(responseData?.current_page || vendorPage + 1),
        page_size: declaredPageSize || ROWS_PER_PAGE,
        total_count: Number(responseData?.total_count || 0),
        total_pages: Number(responseData?.total_pages || 0),
        has_next: Boolean(responseData?.has_next),
        has_previous: Boolean(responseData?.has_previous),
        // Backwards-compatible keys used by existing PaginationComponent patterns.
        current_page_count: Number(responseData?.total_count || 0),
        page_count: Number(responseData?.total_pages || 0),
      });
      // Do not auto-shift UI pagination based on backend `current_page`.
      // We explicitly request the page we want (`page: vendorPage + 1`).
      setApiTotalCount(
        getResponseCount(responseData) ?? Number(invoices.length || 0),
      );
      if (Number.isFinite(declaredPageSize) && declaredPageSize > 0) {
        setServerRowsPerPage(declaredPageSize);
      } else {
        // Keep the default page size stable; do not infer from `invoices.length`
        // because last pages can be shorter and would make the paginator "jump".
        setServerRowsPerPage(ROWS_PER_PAGE);
      }
      if (typeof onSummaryChange === "function") {
        onSummaryChange(buildReconciliationSummary(invoices));
      }
      return invoices;
    } catch (err) {
      console.error("Error fetching invoices", err);
      if (typeof onSummaryChange === "function") {
        onSummaryChange(buildReconciliationSummary([]));
      }
      setPaginationData(null);
      setApiTotalCount(0);
      return [];
    } finally {
      setLoading(false);
    }
  }, [
    AuthToken,
    ApproverToken,
    BuyerToken,
    VendorToken,
    isBuyerMode,
    isApproverMode,
    onSummaryChange,
    search,
    queueStatus,
    refreshToken,
    vendorPage,
  ]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const mergedData = useMemo(() => {
    const local = isApproverMode || vendorPageIndex === 0 ? localInvoices : [];
    return mergeInvoiceRows(local, data);
  }, [data, isApproverMode, localInvoices, vendorPageIndex]);

  const filteredData = useMemo(() => {
    if (isBuyerMode) return mergedData;
    const query = safeLower(search);
    if (!query) return mergedData;

    return mergedData.filter((row) => {
      const status = getOverallComparisonStatus(row);
      const approvalMeta = getApprovalStatusMeta(row);
      return (
        safeLower(getInvoiceNumber(row)).includes(query) ||
        safeLower(getPoNumber(row)).includes(query) ||
        safeLower(status.label).includes(query) ||
        safeLower(approvalMeta.value).includes(query)
      );
    });
  }, [isBuyerMode, mergedData, search]);

  const paginatedData = isApproverMode
    ? filteredData.slice(
        page * ROWS_PER_PAGE,
        page * ROWS_PER_PAGE + ROWS_PER_PAGE,
      )
    : filteredData.slice(0, serverRowsPerPage);

  const selectedStatus = selectedInvoice
    ? getOverallComparisonStatus(selectedInvoice)
    : statusConfig.default;
  const selectedCheckRows = selectedInvoice
    ? getCheckComparisonRows(selectedInvoice)
    : [];
  const selectedLineRows = selectedInvoice
    ? buildLineComparisonRows(selectedInvoice)
    : [];
  const selectedCurrency = selectedInvoice
    ? getCurrency(selectedInvoice)
    : "INR";
  const selectedPoTotal = selectedInvoice ? getPoTotal(selectedInvoice) : null;
  const selectedDifference = selectedInvoice
    ? getTotalDifference(selectedInvoice)
    : null;
  const hasPoComparisonData =
    selectedInvoice &&
    (Object.keys(getVerificationSummary(selectedInvoice)).length > 0 ||
      getPoItems(selectedInvoice).length > 0);

  const selectedApprovalMeta = selectedInvoice
    ? getApprovalStatusMeta(selectedInvoice)
    : getApprovalStatusMeta({});

  const selectedApproverStep = useMemo(() => {
    if (!selectedInvoice || !ApproverDesignation) return null;
    return getApprovalStepForDesignation(selectedInvoice, ApproverDesignation);
  }, [ApproverDesignation, selectedInvoice]);

  const selectedApproverStepStatus = safeLower(selectedApproverStep?.status);
  const selectedApproverStepRemarks = safeString(
    selectedApproverStep?.remarks,
  ).trim();
  const approverStepIsFinal =
    selectedApproverStepStatus === "approved" ||
    selectedApproverStepStatus === "rejected";
  const hasApprovalRemarks = String(approvalRemarks || "").trim().length > 0;

  const approvalEligible = useMemo(() => {
    if (!isApproverMode || !selectedInvoice) return false;

    const comparisonStatus = getOverallComparisonStatus(selectedInvoice);
    if (comparisonStatus?.type !== "matched") return false;

    const approvalStatusRaw =
      pick(selectedInvoice, ["approval_status", "approval_status_label"]) || "";
    const approvalStatus = safeLower(approvalStatusRaw);
    if (
      approvalStatus.includes("reject") ||
      approvalStatus.includes("approve")
    ) {
      return false;
    }

    const pendingWith = safeLower(pick(selectedInvoice, ["pending_with"]));
    const approverDesignation = safeLower(ApproverDesignation);
    if (
      pendingWith &&
      approverDesignation &&
      pendingWith !== approverDesignation
    ) {
      return false;
    }

    return Boolean(pick(selectedInvoice, ["invoice_token", "token"]));
  }, [ApproverDesignation, isApproverMode, selectedInvoice]);

  const approvalGateMessage = useMemo(() => {
    if (!isApproverMode || !selectedInvoice) return "";

    const comparisonStatus = getOverallComparisonStatus(selectedInvoice);
    if (comparisonStatus?.type !== "matched") {
      return "You can approve/reject only after the invoice is matched.";
    }

    if (approverStepIsFinal) {
      return selectedApproverStepStatus === "approved"
        ? "You have already approved this invoice."
        : "You have already rejected this invoice.";
    }

    const pendingWithLabel = pick(selectedInvoice, ["pending_with"]);
    const pendingWith = safeLower(pendingWithLabel);
    const approverDesignation = safeLower(ApproverDesignation);
    if (
      pendingWith &&
      approverDesignation &&
      pendingWith !== approverDesignation
    ) {
      return `Waiting for ${pendingWithLabel} approval. You can act once it is pending with ${ApproverDesignation}.`;
    }

    return "You can approve/reject only when the invoice is matched and pending with your designation.";
  }, [
    ApproverDesignation,
    approverStepIsFinal,
    isApproverMode,
    selectedApproverStepStatus,
    selectedInvoice,
  ]);

  const handleApproveReject = useCallback(
    async (action) => {
      if (!selectedInvoice || !approvalEligible) return;

      const invoiceToken = pick(selectedInvoice, ["invoice_token", "token"]);
      if (!invoiceToken) return;

      const trimmedRemarks = String(approvalRemarks || "").trim();
      if (!trimmedRemarks) {
        setApprovalActionError("Please enter comments to approve or reject.");
        setApprovalActionSuccess("");
        return;
      }

      if (!ApproverToken) {
        setApprovalActionError(
          "Approver token not available. Please re-login and try again.",
        );
        setApprovalActionSuccess("");
        return;
      }

      setApprovalActionLoading(true);
      setApprovalActionError("");
      setApprovalActionSuccess("");

      try {
        const payload = {
          invoice_token: invoiceToken,
          approver_token: ApproverToken,
          action,
          remarks: trimmedRemarks || null,
        };

        const config = AuthToken
          ? { headers: { Authorization: `Bearer ${AuthToken}` } }
          : undefined;

        await axios.post(
          API_ENDPOINT.approve_vendor_invoice_document,
          payload,
          config,
        );
        setApprovalActionSuccess(
          action === "approve"
            ? "Invoice approved successfully."
            : "Invoice rejected successfully.",
        );
        setApprovalRemarks(trimmedRemarks);
        const updatedInvoices = await fetchInvoices();
        const normalizedToken = normalizeToken(invoiceToken);
        const latest = updatedInvoices.find((row) => {
          const token = pick(row, ["invoice_token", "token"]);
          return normalizeToken(token) === normalizedToken;
        });
        if (latest) setSelectedInvoice(latest);
      } catch (err) {
        console.error("Invoice approval action failed", err);
        setApprovalActionError(
          err?.response?.data?.message ||
            err?.response?.data?.error ||
            "Failed to update approval status. Please try again.",
        );
      } finally {
        setApprovalActionLoading(false);
      }
    },
    [
      approvalEligible,
      approvalRemarks,
      AuthToken,
      ApproverToken,
      fetchInvoices,
      selectedInvoice,
    ],
  );

  useEffect(() => {
    if (!openModal) return;
    setApprovalRemarks("");
    setApprovalActionError("");
    setApprovalActionSuccess("");
  }, [openModal]);

  useEffect(() => {
    const chatActionRaw = sessionStorage.getItem("CHAT_ACTION");
    if (!chatActionRaw) return;

    const { action, invoice_process_code } = JSON.parse(chatActionRaw);
    const query = safeLower(invoice_process_code);

    if (action === "search_invoices") {
      setSearch(invoice_process_code || "");
      if (isApproverMode) setPage(0);
      else setPageNumber(1);
      sessionStorage.removeItem("CHAT_ACTION");
      return;
    }

    if (action === "open_specific_invoice" && query) {
      const matchingInvoice = mergedData.find(
        (row) =>
          safeLower(getInvoiceNumber(row)).includes(query) ||
          safeLower(getPoNumber(row)).includes(query),
      );

      if (matchingInvoice) {
        setSelectedInvoice(matchingInvoice);
        setOpenModal(true);
        sessionStorage.removeItem("CHAT_ACTION");
      }
    }
  }, [mergedData, isApproverMode]);

  const handleOpenComparison = (invoice) => {
    setSelectedInvoice(invoice);
    setOpenModal(true);
  };

  const handleOpenLineCompare = (lineRow) => {
    setSelectedLineItem(lineRow);
    setOpenLineDrawer(true);
  };

  const handleCloseLineCompare = () => {
    setOpenLineDrawer(false);
    setSelectedLineItem(null);
  };

  const lineIssueLabelMap = {
    po_missing: "PO Missing",
    name: "Name",
    description: "Desc",
    qty: "Quantity",
    unit_price: "Price",
    total: "Total",
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mb: 2,
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ width: 320, maxWidth: "100%" }}>
          <SearchBar
            value={search}
            onChange={(value) => {
              setSearch(value);
              if (isApproverMode) setPage(0);
              else setPageNumber(1);
              try {
                localStorage.setItem(pageStorageKey, "1");
              } catch {}
            }}
          />
        </Box>

        <Typography
          sx={{
            fontFamily: "gilroyMedium",
            fontSize: "14px",
            color: "#64748b",
          }}
        >
          Total Invoices:{" "}
          <Box
            component="span"
            sx={{ fontFamily: "GilroyBold", color: "#0f172a" }}
          >
            {isApproverMode ? filteredData.length : apiTotalCount}
          </Box>
        </Typography>
      </Box>

      <Paper
        elevation={2}
        sx={{
          borderRadius: "10px",
          boxShadow: "0 2px 12px rgba(0, 0, 0, 0.08)",
          overflow: "hidden",
          mt: 3,
        }}
      >
        <TableContainer
          sx={{
            maxHeight: "60vh",
            overflowX: "auto",
            overflowY: "auto",
            WebkitOverflowScrolling: "touch",
            position: "relative",
          }}
        >
          <Table
            size="small"
            stickyHeader
            sx={{
              minWidth: isBuyerMode ? 1650 : 1320,
              tableLayout: "fixed",
              borderRight: "1px solid #e2e8f0",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    py: 1.4,
                    borderBottom: "2px solid #e2e8f0",
                    fontFamily: "GilroyBold",
                    fontSize: "14px",
                    color: "#334155",
                    backgroundColor: "#f8fafc",
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableCell
                  sx={{
                    position: "sticky",
                    left: 0,
                    zIndex: 5,
                    width: stickyPoColumnWidth,
                    minWidth: stickyPoColumnWidth,
                    maxWidth: stickyPoColumnWidth,
                    backgroundColor: "#e0e9ee",
                    boxShadow: "2px 0 0 0 #e2e8f0 inset",
                    px: 1.8,
                  }}
                >
                  PO ID
                </TableCell>
                <TableCell sx={{ width: 180 }}>Invoice Number</TableCell>
                {isBuyerMode && <TableCell sx={{ width: 240 }}>Vendor Name</TableCell>}
                {isBuyerMode && <TableCell sx={{ width: 240 }}>Approvers</TableCell>}
                <TableCell sx={{ width: 150 }}>Invoice Date</TableCell>
                <TableCell sx={{ width: 110 }}>Items</TableCell>
                <TableCell sx={{ width: 170 }}>Invoice Total</TableCell>
                <TableCell sx={{ width: 170 }}>Match Status</TableCell>
                <TableCell sx={{ width: 170 }}>Approval Status</TableCell>
                <TableCell sx={{ width: 180 }}>Uploaded On</TableCell>
                <TableCell
                  sx={{
                    position: "sticky",
                    right: 0,
                    zIndex: 6,
                    width: stickyActionColumnWidth,
                    minWidth: stickyActionColumnWidth,
                    maxWidth: stickyActionColumnWidth,
                    textAlign: "center",
                    backgroundColor: "#e0e9ee",
                    boxShadow: "-2px 0 0 0 #e2e8f0 inset",
                    px: 0.8,
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {loading && !mergedData.length ? (
                <TableRow>
                  <TableCell colSpan={isBuyerMode ? 11 : 9} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : (
                <>
                  {paginatedData.map((row, idx) => {
                    const comparisonStatus = getOverallComparisonStatus(row);
                    const approvalMeta = getApprovalStatusMeta(row);
                    const rowKey = getRowKey(row, idx);

                    return (
                      <TableRow
                        key={rowKey}
                        sx={{
                          "&:hover td": { backgroundColor: "#f8fafc" },
                          "& td": {
                            py: 1.8,
                            px: 1.2,
                            borderBottom: "1px solid #f1f5f9",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            fontFamily: "gilroyMedium",
                            fontSize: "14px",
                            color: "#475569",
                          },
                        }}
                      >
                        <TableCell
                          title={safeString(getPoNumber(row))}
                          sx={{
                            position: "sticky",
                            left: 0,
                            zIndex: 3,
                            width: stickyPoColumnWidth,
                            minWidth: stickyPoColumnWidth,
                            maxWidth: stickyPoColumnWidth,
                            backgroundColor: "#f8fafc",
                            boxShadow: "2px 0 0 0 #e2e8f0 inset",
                            fontFamily: "GilroyBold !important",
                            color: "#0f172a !important",
                            px: 1.8,
                          }}
                        >
                          {getPoNumber(row)}
                        </TableCell>
                        <TableCell title={safeString(getInvoiceNumber(row))}>
                          {getInvoiceNumber(row)}
                        </TableCell>
                        {isBuyerMode && (
                          <TableCell title={safeString(row?.vendor_name)}>
                            {valueOrDash(row?.vendor_name)}
                          </TableCell>
                        )}
                        {isBuyerMode && (
                          <TableCell title={getBuyerApprovers(row)}>
                            {getBuyerApprovers(row)}
                          </TableCell>
                        )}
                        <TableCell>{formatDate(getInvoiceDate(row))}</TableCell>
                        <TableCell>{getItemCount(row)}</TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "GilroyBold !important",
                            color: "#075b97 !important",
                          }}
                        >
                          {formatCurrency(
                            getInvoiceTotal(row),
                            getCurrency(row),
                          )}
                        </TableCell>
                        <TableCell>
                          <StatusChip status={comparisonStatus} />
                        </TableCell>
                        <TableCell>
                          <Tooltip
                            title={
                              <Typography
                                sx={{
                                  whiteSpace: "pre-line",
                                  fontFamily: "gilroyMedium",
                                  fontSize: "12px",
                                }}
                              >
                                {getApprovalTooltipContent(row)}
                              </Typography>
                            }
                            arrow
                            classes={{ tooltip: styles.gilroyTooltip }}
                          >
                            <Chip
                              label={approvalMeta.value}
                              size="small"
                              sx={{
                                height: 26,
                                fontFamily: "GilroyBold",
                                fontSize: "12px",
                                color: approvalMeta.color,
                                backgroundColor: approvalMeta.bg,
                                border: `1px solid ${approvalMeta.border}`,
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell>{formatDateTime(getUploadedTimestamp(row))}</TableCell>
                        <TableCell
                          sx={{
                            position: "sticky",
                            right: 0,
                            zIndex: 4,
                            width: stickyActionColumnWidth,
                            minWidth: stickyActionColumnWidth,
                            maxWidth: stickyActionColumnWidth,
                            backgroundColor: "#f8fafc",
                            textAlign: "center",
                            boxShadow: "-2px 0 0 0 #e2e8f0 inset",
                            px: 0.8,
                          }}
                        >
                          <Tooltip
                            title="View invoice comparison"
                            arrow
                            classes={{ tooltip: styles.gilroyTooltip }}
                          >
                            <IconButton
                              size="large"
                              onClick={() => handleOpenComparison(row)}
                              sx={{ p: 0.8 }}
                            >
                              <Box
                                component="img"
                                src={viewIcon}
                                alt="View"
                                sx={{ width: 20, height: 20 }}
                              />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  {!paginatedData.length && (
                    <TableRow>
                      <TableCell
                        colSpan={isBuyerMode ? 11 : 9}
                        align="center"
                        sx={{
                          py: 7,
                          fontFamily: "GilroyBold",
                          fontSize: "17px",
                          color: "#64748b",
                        }}
                      >
                        No invoices found
                      </TableCell>
                    </TableRow>
                  )}
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {isApproverMode ? (
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            rowsPerPage={ROWS_PER_PAGE}
            rowsPerPageOptions={[ROWS_PER_PAGE]}
            onPageChange={(_, newPage) => setPage(newPage)}
            sx={{
              borderTop: "1px solid #e2e8f0",
              "& .MuiTablePagination-toolbar": {
                minHeight: 58,
                px: 2,
              },
              "& .MuiTablePagination-displayedRows": {
                fontFamily: "gilroyMedium",
                fontSize: "14px",
              },
            }}
          />
        ) : (
          <Box sx={{ borderTop: "1px solid #e2e8f0", px: 2, py: 1.5 }}>
            <PaginantionComponent
              pageNumber={pageNumber}
              currentPage={paginationData?.current_page || 1}
              itemsPerPage={paginationData?.total_count ?? apiTotalCount ?? 0}
              totalItems={paginationData?.total_pages ?? 0}
              localStorageKey={pageStorageKey}
              previouPageClick={() => {
                previouPageClick(parseInt(pageNumber, 10) || 1, setPageNumber);
                try {
                  localStorage.setItem(
                    pageStorageKey,
                    String(
                      Math.max(
                        1,
                        (parseInt(String(pageNumber || 1), 10) || 1) - 1,
                      ),
                    ),
                  );
                } catch {}
              }}
              nextPageClick={() => {
                nextPageClick(
                  parseInt(pageNumber, 10) || 1,
                  setPageNumber,
                  paginationData,
                );
                try {
                  localStorage.setItem(
                    pageStorageKey,
                    String((parseInt(String(pageNumber || 1), 10) || 1) + 1),
                  );
                } catch {}
              }}
              setPageNumber={setPageNumber}
              disablePrevious={
                parseInt(pageNumber, 10) === 1 || pageNumber === ""
              }
              disableNext={
                parseInt(pageNumber, 10) === paginationData?.page_count ||
                !paginationData ||
                pageNumber === ""
              }
            />
          </Box>
        )}
      </Paper>

      <Dialog
        open={openModal}
        onClose={() => {
          setOpenModal(false);
          handleCloseLineCompare();
        }}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 3,
            py: 3,
            borderBottom: "1px solid #e2e8f0",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
          >
            <Box>
              <Typography
                sx={{
                  fontFamily: "GilroyBold",
                  fontSize: "20px",
                  color: "#0f172a",
                }}
              >
                PO vs Invoice Status
              </Typography>
              <Typography
                sx={{
                  mt: 0.4,
                  fontFamily: "gilroyMedium",
                  fontSize: "14px",
                  color: "#64748b",
                }}
              >
                Invoice{" "}
                {selectedInvoice ? getInvoiceNumber(selectedInvoice) : "-"}{" "}
                against PO{" "}
                {selectedInvoice ? getPoNumber(selectedInvoice) : "-"}
              </Typography>
            </Box>
            <StatusChip status={selectedStatus} />
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ p: 3, backgroundColor: "#f8fafc", mt: 2 }}>
          {selectedInvoice && (
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
                <SummaryCard
                  label="PO ID"
                  value={getPoNumber(selectedInvoice)}
                  highlight
                />
                <SummaryCard
                  label="Invoice Number"
                  value={getInvoiceNumber(selectedInvoice)}
                />
                <SummaryCard
                  label="Invoice Date"
                  value={formatDate(getInvoiceDate(selectedInvoice))}
                />
                <SummaryCard
                  label="Invoice Total"
                  value={formatCurrency(
                    getInvoiceTotal(selectedInvoice),
                    selectedCurrency,
                  )}
                  highlight
                />
                <SummaryCard
                  label="PO Total"
                  value={formatCurrency(selectedPoTotal, selectedCurrency)}
                />
                <SummaryCard
                  label="Difference"
                  value={formatCurrency(selectedDifference, selectedCurrency)}
                />
              </Stack>

              {!hasPoComparisonData && (
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 2,
                    border: "1px solid #fde68a",
                    background: "#fffbeb",
                    color: "#92400e",
                    fontFamily: "gilroyMedium",
                    fontSize: "14px",
                  }}
                >
                  PO comparison details are not available in this invoice
                  response. The uploaded invoice values are shown below.
                </Box>
              )}

              <Box>
                <Typography
                  sx={{
                    mb: 1,
                    fontFamily: "GilroyBold",
                    fontSize: "16px",
                    color: "#0f172a",
                  }}
                >
                  Verification Checks
                </Typography>
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    boxShadow: "none",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow
                        sx={{
                          "& th": {
                            backgroundColor: "#f8fafc",
                            fontFamily: "GilroyBold",
                            fontSize: "14px",
                            color: "#334155",
                            whiteSpace: "nowrap",
                          },
                        }}
                      >
                        <TableCell>Check</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Uploaded Invoice</TableCell>
                        <TableCell>PO</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedCheckRows.map((row) => (
                        <TableRow key={row.label}>
                          <TableCell
                            sx={{ fontFamily: "GilroyBold", fontSize: "14px" }}
                          >
                            {row.label}
                          </TableCell>
                          <TableCell>
                            <StatusChip status={row.status} />
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "gilroyMedium",
                              fontSize: "14px",
                            }}
                          >
                            {valueOrDash(row.invoiceValue)}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "gilroyMedium",
                              fontSize: "14px",
                            }}
                          >
                            {valueOrDash(row.poValue)}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "gilroyMedium",
                              fontSize: "14px",
                              color: "#64748b",
                              maxWidth: 360,
                            }}
                          >
                            {valueOrDash(row.detail)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Box>
                <Typography
                  sx={{
                    mb: 1,
                    fontFamily: "GilroyBold",
                    fontSize: "16px",
                    color: "#0f172a",
                  }}
                >
                  Line Item Comparison
                </Typography>
                <TableContainer
                  component={Paper}
                  sx={{
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    boxShadow: "none",
                    maxHeight: 420,
                  }}
                >
                  <Table
                    size="small"
                    stickyHeader
                    sx={{
                      tableLayout: "fixed",
                      width: "100%",
                    }}
                  >
                    <TableHead>
                      <TableRow
                        sx={{
                          "& th": {
                            backgroundColor: "#f8fafc",
                            fontFamily: "GilroyBold",
                            fontSize: "14px",
                            color: "#334155",
                            whiteSpace: "nowrap",
                          },
                        }}
                      >
                        <TableCell>Item Code</TableCell>
                        <TableCell>Item Name</TableCell>
                        <TableCell
                          sx={{
                            width: 180,
                            minWidth: 180,
                            maxWidth: 180,
                          }}
                        >
                          Description
                        </TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Line Total</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Issues</TableCell>
                        <TableCell align="center">Compare</TableCell>
                        <TableCell>Details</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedLineRows.map((row) => (
                        <TableRow
                          key={row.key}
                          hover
                          onClick={() => handleOpenLineCompare(row)}
                          sx={{ cursor: "pointer" }}
                        >
                          <TableCell
                            sx={{
                              fontFamily: "gilroyMedium",
                              fontSize: "14px",
                            }}
                          >
                            {row.itemCode}
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "gilroyMedium",
                              fontSize: "14px",
                              minWidth: 220,
                            }}
                          >
                            <Typography
                              component="div"
                              title={safeString(row.invoiceItemName)}
                              sx={{
                                fontFamily: "gilroyMedium",
                                fontSize: "14px",
                                color: "#0f172a",
                                lineHeight: 1.2,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {valueOrDash(row.invoiceItemName)}
                            </Typography>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "gilroyMedium",
                              fontSize: "14px",
                              width: 180,
                              minWidth: 180,
                              maxWidth: 180,
                              overflow: "hidden",
                            }}
                          >
                            {(() => {
                              const desc = safeString(row.invoiceDescription);
                              const isLong = desc.length > 80;
                              const displayText = isLong
                                ? `${desc.slice(0, 80)}…`
                                : valueOrDash(row.invoiceDescription);

                              return (
                                <Stack
                                  spacing={0.25}
                                  sx={{ minWidth: 0, width: "100%" }}
                                >
                                  <Typography
                                    component="div"
                                    title={desc}
                                    sx={{
                                      fontFamily: "gilroyMedium",
                                      fontSize: "14px",
                                      color: "#0f172a",
                                      lineHeight: 1.2,
                                      whiteSpace: "nowrap",
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      width: "100%",
                                      maxWidth: "100%",
                                    }}
                                  >
                                    {displayText}
                                  </Typography>

                                  {isLong && (
                                    <Typography
                                      component="button"
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenLineCompare(row);
                                      }}
                                      sx={{
                                        all: "unset",
                                        cursor: "pointer",
                                        fontFamily: "gilroyMedium",
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        color: "#2563eb",
                                        "&:hover": {
                                          textDecoration: "underline",
                                        },
                                        width: "fit-content",
                                      }}
                                    >
                                      Read more
                                    </Typography>
                                  )}
                                </Stack>
                              );
                            })()}
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 70 }}>
                            <Typography
                              sx={{
                                fontFamily: "gilroyMedium",
                                fontSize: "14px",
                              }}
                            >
                              {formatNumber(row.invoiceQty)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 100 }}>
                            <Typography
                              sx={{
                                fontFamily: "gilroyMedium",
                                fontSize: "14px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatCurrency(
                                row.invoiceRate,
                                selectedCurrency,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ minWidth: 110 }}>
                            <Typography
                              sx={{
                                fontFamily: "gilroyMedium",
                                fontSize: "14px",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {formatCurrency(
                                row.invoiceTotal,
                                selectedCurrency,
                              )}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <StatusChip status={row.status} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 160 }}>
                            {row.issueKeys?.length ? (
                              <Stack
                                direction="row"
                                spacing={0.8}
                                sx={{ flexWrap: "wrap" }}
                              >
                                {row.issueKeys.slice(0, 4).map((key) => (
                                  <Chip
                                    key={key}
                                    label={lineIssueLabelMap[key] || key}
                                    size="small"
                                    sx={{
                                      height: 26,
                                      fontFamily: "gilroyMedium",
                                      fontSize: "13px",
                                      fontWeight: 700,
                                      backgroundColor: "#fff7ed",
                                      border: "1px solid #fed7aa",
                                      color: "#9a3412",
                                    }}
                                  />
                                ))}
                                {row.issueKeys.length > 4 && (
                                  <Chip
                                    label={`+${row.issueKeys.length - 4}`}
                                    size="small"
                                    sx={{
                                      height: 26,
                                      fontFamily: "gilroyMedium",
                                      fontSize: "13px",
                                      fontWeight: 700,
                                      backgroundColor: "#f1f5f9",
                                      border: "1px solid #e2e8f0",
                                      color: "#475569",
                                    }}
                                  />
                                )}
                              </Stack>
                            ) : (
                              <Typography
                                sx={{
                                  fontFamily: "gilroyMedium",
                                  fontSize: "14px",
                                  color: "#94a3b8",
                                }}
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell
                            align="center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Tooltip
                              title="Compare with PO"
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    fontSize: "14px",
                                    fontFamily: "gilroyMedium",
                                  },
                                },
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleOpenLineCompare(row)}
                                sx={{
                                  border: "1px solid #e2e8f0",
                                  borderRadius: 2,
                                }}
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                          <TableCell
                            sx={{
                              fontFamily: "gilroyMedium",
                              fontSize: "14px",
                              color: "#64748b",
                              minWidth: 260,
                              maxWidth: 520,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            <Tooltip
                              title={safeString(row.detail)}
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: {
                                    fontSize: "14px",
                                    fontFamily: "gilroyMedium",
                                  },
                                },
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  display: "inline-block",
                                  maxWidth: "100%",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  cursor: "help",
                                }}
                              >
                                {row.detail}
                              </Box>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}

                      {!selectedLineRows.length && (
                        <TableRow>
                          <TableCell
                            colSpan={10}
                            align="center"
                            sx={{
                              py: 4,
                              fontFamily: "GilroyBold",
                              fontSize: "15px",
                              color: "#64748b",
                            }}
                          >
                            No line items returned for this invoice
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>

              <Paper
                sx={{
                  p: 2.5,
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  boxShadow: "none",
                }}
              >
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: "GilroyBold",
                        fontSize: "17px",
                        color: "#0f172a",
                      }}
                    >
                      Approval Status
                    </Typography>
                    <Typography
                      sx={{
                        mt: 0.6,
                        fontFamily: "gilroyMedium",
                        fontSize: "15px",
                        color: "#475569",
                        whiteSpace: "pre-line",
                      }}
                    >
                      {getApprovalTooltipContent(selectedInvoice)}
                    </Typography>
                  </Box>
                  <Chip
                    label={selectedApprovalMeta.value}
                    size="small"
                    sx={{
                      height: 34,
                      px: 0.8,
                      fontFamily: "GilroyBold",
                      fontSize: "13px",
                      color: selectedApprovalMeta.color,
                      backgroundColor: selectedApprovalMeta.bg,
                      border: `1px solid ${selectedApprovalMeta.border}`,
                    }}
                  />
                </Stack>

                {isApproverMode && (
                  <>
                    <Divider sx={{ my: 2.2 }} />

                    {(approverStepIsFinal || selectedApproverStepRemarks) && (
                      <Box
                        sx={{
                          mb: 2,
                          p: 1.8,
                          borderRadius: 2,
                          border: "1px solid #e2e8f0",
                          backgroundColor: "#f8fafc",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "GilroyBold",
                            fontSize: "14px",
                            color: "#0f172a",
                          }}
                        >
                          {selectedApproverStepStatus === "approved"
                            ? "You approved this invoice"
                            : selectedApproverStepStatus === "rejected"
                              ? "You rejected this invoice"
                              : "Your remarks"}
                        </Typography>
                        {!!selectedApproverStepRemarks && (
                          <Typography
                            sx={{
                              mt: 0.8,
                              fontFamily: "gilroyMedium",
                              fontSize: "15px",
                              color: "#334155",
                              whiteSpace: "pre-line",
                            }}
                          >
                            {selectedApproverStepRemarks}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {approverStepIsFinal ? (
                      <Stack
                        direction="row"
                        spacing={1.5}
                        alignItems="center"
                        justifyContent="flex-end"
                      >
                        <Button
                          size="large"
                          variant="contained"
                          disabled
                          sx={{
                            minWidth: 150,
                            height: 44,
                            borderRadius: 2,
                            fontFamily: "GilroyBold",
                            fontSize: "15px",
                            textTransform: "none",
                            color:
                              selectedApproverStepStatus === "approved"
                                ? "#14532d"
                                : "#7f1d1d",
                            backgroundColor:
                              selectedApproverStepStatus === "approved"
                                ? "#dcfce7"
                                : "#fee2e2",
                            border: `1px solid ${
                              selectedApproverStepStatus === "approved"
                                ? "#86efac"
                                : "#fecaca"
                            }`,
                            "&.Mui-disabled": {
                              color:
                                selectedApproverStepStatus === "approved"
                                  ? "#14532d"
                                  : "#7f1d1d",
                              backgroundColor:
                                selectedApproverStepStatus === "approved"
                                  ? "#dcfce7"
                                  : "#fee2e2",
                              borderColor:
                                selectedApproverStepStatus === "approved"
                                  ? "#86efac"
                                  : "#fecaca",
                            },
                          }}
                        >
                          {selectedApproverStepStatus === "approved"
                            ? "Approved"
                            : "Rejected"}
                        </Button>
                      </Stack>
                    ) : (
                      <>
                        <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: "#ffffff", border: "1px solid #e2e8f0", borderRadius: "12px", boxShadow: "0 2px 8px #0f172a06" }}>
                          <Typography id="invoice-comments-label" component="h3" sx={{ mb: 0.75, fontSize: "16px", fontFamily: "GilroyBold", color: "#0f172a" }}>
                            Comments
                          </Typography>
                          <Typography id="invoice-comments-help" sx={{ mb: 2, fontSize: "14px", fontFamily: "GilroyMedium", color: "#64748b" }}>
                            Add a comment before approving or rejecting this invoice.
                          </Typography>
                        <TextField
                          placeholder="Enter your comments..."
                          inputProps={{ "aria-labelledby": "invoice-comments-label", "aria-describedby": "invoice-comments-help" }}
                          fullWidth
                          multiline
                          rows={4}
                          value={approvalRemarks}
                          onChange={(e) => {
                            setApprovalRemarks(e.target.value);
                            if (approvalActionError) setApprovalActionError("");
                          }}
                          disabled={!approvalEligible || approvalActionLoading}
                          required
                          error={approvalEligible && !hasApprovalRemarks}
                          helperText={
                            approvalEligible && !hasApprovalRemarks
                              ? "Comments are required to approve or reject."
                              : ""
                          }
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              backgroundColor: "#f8fafc",
                              borderRadius: "10px",
                              p: 2,
                              "& fieldset": { borderColor: "#dbe3ec" },
                              "&:hover fieldset": { borderColor: "#94a3b8" },
                              "&.Mui-focused": { backgroundColor: "#ffffff", boxShadow: "0 0 0 3px #2563eb14" },
                              "&.Mui-focused fieldset": { borderColor: "#2563eb" },
                              "&.Mui-error fieldset": { borderColor: "#d32f2f" },
                            },
                            "& .MuiInputBase-input": {
                              fontFamily: "GilroyMedium",
                              fontSize: "14px",
                              lineHeight: 1.6,
                              color: "#0f172a",
                              "&::placeholder": { color: "#64748b", opacity: 1 },
                            },
                            "& .MuiFormHelperText-root": { fontSize: "14px" },
                          }}
                        />
                        </Box>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="flex-end"
                          sx={{ mt: 2, flexWrap: "wrap", gap: 1.5, "& > .MuiButton-root": { flex: { xs: "1 1 120px", sm: "0 0 auto" } } }}
                        >
                          <Button
                            size="large"
                            variant="contained"
                            startIcon={approvalActionLoading ? <CircularProgress size={18} color="inherit" /> : <CheckCircleOutline />}
                            onClick={() => handleApproveReject("approve")}
                            disabled={
                              !approvalEligible ||
                              approvalActionLoading ||
                              !hasApprovalRemarks
                            }
                            sx={{
                              ...approvalButtonSx,
                              backgroundColor: "#15803d",
                              boxShadow: "none",
                              "&:hover": {
                                backgroundColor: "#166534",
                                boxShadow: "0 8px 18px rgba(37, 111, 74, 0.18)",
                              },
                            }}
                          >
                            {approvalActionLoading ? "Processing..." : "Approve"}
                          </Button>
                          <Button
                            size="large"
                            variant="outlined"
                            color="error"
                            startIcon={<HighlightOff />}
                            onClick={() => handleApproveReject("reject")}
                            disabled={
                              !approvalEligible ||
                              approvalActionLoading ||
                              !hasApprovalRemarks
                            }
                            sx={{
                              ...approvalButtonSx,
                              color: "#b42318",
                              borderColor: "#fda29b",
                              backgroundColor: "#fff5f4",
                              "&:hover": {
                                borderColor: "#b42318",
                                backgroundColor: "#fee4e2",
                                boxShadow: "none",
                              },
                            }}
                          >
                            Reject
                          </Button>
                        </Stack>
                      </>
                    )}

                    {!approverStepIsFinal && !approvalEligible && (
                      <Typography
                        sx={{
                          mt: 1.6,
                          fontFamily: "gilroyMedium",
                          fontSize: "15px",
                          color: "#64748b",
                        }}
                      >
                        {approvalGateMessage}
                      </Typography>
                    )}

                    {!!approvalActionError && (
                      <Typography
                        sx={{
                          mt: 1.6,
                          fontFamily: "gilroyMedium",
                          fontSize: "15px",
                          color: "#b91c1c",
                        }}
                      >
                        {approvalActionError}
                      </Typography>
                    )}

                    {!!approvalActionSuccess && (
                      <Typography
                        sx={{
                          mt: 1.6,
                          fontFamily: "gilroyMedium",
                          fontSize: "15px",
                          color: "#166534",
                        }}
                      >
                        {approvalActionSuccess}
                      </Typography>
                    )}
                  </>
                )}
              </Paper>

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setOpenModal(false);
                    handleCloseLineCompare();
                  }}
                  sx={{
                    minWidth: 120,
                    height: 40,
                    borderRadius: 2,
                    textTransform: "none",
                    fontFamily: "GilroyBold",
                    fontSize: "14px",
                  }}
                >
                  Close
                </Button>
              </Box>
            </Stack>
          )}
        </DialogContent>
      </Dialog>

      <Drawer
        anchor="right"
        open={openLineDrawer}
        onClose={handleCloseLineCompare}
        sx={{ zIndex: (theme) => theme.zIndex.modal + 20 }}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 680, md: 760 },
            p: 2.5,
            zIndex: (theme) => theme.zIndex.modal + 21,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{
                fontFamily: "GilroyBold",
                fontSize: "16px",
                color: "#0f172a",
              }}
            >
              Line Item Compare
            </Typography>
            <Typography
              sx={{
                fontFamily: "gilroyMedium",
                fontSize: "14px",
                color: "#64748b",
              }}
            >
              Item Code: {valueOrDash(selectedLineItem?.itemCode)}
            </Typography>
          </Box>
          <StatusChip status={selectedLineItem?.status} />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "120px minmax(0, 1fr) minmax(0, 1.3fr)",
            gap: 1.5,
          }}
        >
          <Box />
          <Typography
            sx={{
              fontFamily: "GilroyBold",
              fontSize: "14px",
              color: "#0f172a",
            }}
          >
            Invoice
          </Typography>
          <Typography
            sx={{
              fontFamily: "GilroyBold",
              fontSize: "14px",
              color: "#0f172a",
            }}
          >
            PO
          </Typography>
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {[
          {
            key: "name",
            label: "Item Name",
            invoice: selectedLineItem?.invoiceItemName,
            po: selectedLineItem?.poItemName,
          },
          {
            key: "description",
            label: "Description",
            invoice: selectedLineItem?.invoiceDescription,
            po: selectedLineItem?.poDescription,
          },
          {
            key: "qty",
            label: "Quantity",
            invoice: formatNumber(selectedLineItem?.invoiceQty),
            po: formatNumber(selectedLineItem?.poQty),
          },
          {
            key: "unit_price",
            label: "Unit Price",
            invoice: formatCurrency(
              selectedLineItem?.invoiceRate,
              selectedCurrency,
            ),
            po: formatCurrency(selectedLineItem?.poRate, selectedCurrency),
          },
          {
            key: "total",
            label: "Line Total",
            invoice: formatCurrency(
              selectedLineItem?.invoiceTotal,
              selectedCurrency,
            ),
            po: formatCurrency(selectedLineItem?.poTotal, selectedCurrency),
          },
        ].map((row) => {
          const isMismatch = Boolean(
            selectedLineItem?.mismatchFlags?.[row.key],
          );
          const valueSx = {
            p: 1.1,
            borderRadius: 2,
            border: "1px solid",
            borderColor: isMismatch ? "#fecaca" : "#e2e8f0",
            backgroundColor: isMismatch ? "#fff1f2" : "#ffffff",
            fontFamily: "gilroyMedium",
            fontSize: "14px",
            color: "#0f172a",
            wordBreak: "break-word",
            overflowWrap: "anywhere",
            ...(row.key === "description"
              ? {
                  maxHeight: 240,
                  overflowY: "auto",
                  whiteSpace: "pre-wrap",
                  lineHeight: 1.5,
                }
              : {
                  whiteSpace: "normal",
                  lineHeight: 1.35,
                }),
          };

          return (
            <Box
              key={row.key}
              sx={{
                display: "grid",
                gridTemplateColumns: "120px minmax(0, 1fr) minmax(0, 1.3fr)",
                gap: 1.5,
                alignItems: "start",
                mb: 1.2,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "GilroyBold",
                  fontSize: "14px",
                  color: "#334155",
                  pt: 1,
                }}
              >
                {row.label}
              </Typography>
              <Box sx={valueSx}>{valueOrDash(row.invoice)}</Box>
              <Box sx={valueSx}>{valueOrDash(row.po)}</Box>
            </Box>
          );
        })}

        <Divider sx={{ my: 1.5 }} />

        <Box>
          <Typography
            sx={{
              fontFamily: "GilroyBold",
              fontSize: "14px",
              color: "#0f172a",
              mb: 1,
            }}
          >
            Issues
          </Typography>
          {selectedLineItem?.issueKeys?.length ? (
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              {selectedLineItem.issueKeys.map((key) => (
                <Chip
                  key={key}
                  label={lineIssueLabelMap[key] || key}
                  size="small"
                  sx={{
                    height: 28,
                    fontFamily: "gilroyMedium",
                    fontSize: "13px",
                    fontWeight: 700,
                    backgroundColor: "#fff7ed",
                    border: "1px solid #fed7aa",
                    color: "#9a3412",
                  }}
                />
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{
                fontFamily: "gilroyMedium",
                fontSize: "14px",
                color: "#64748b",
              }}
            >
              No issues found for this line item.
            </Typography>
          )}
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography
            sx={{
              fontFamily: "GilroyBold",
              fontSize: "14px",
              color: "#0f172a",
              mb: 0.8,
            }}
          >
            Details
          </Typography>
          <Typography
            sx={{
              fontFamily: "gilroyMedium",
              fontSize: "14px",
              color: "#475569",
            }}
          >
            {valueOrDash(selectedLineItem?.detail)}
          </Typography>
        </Box>
      </Drawer>
    </>
  );
};

export default InvoiceTable;
